import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService, MenuItem } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import {
  AddFormComponent,
  FormField,
} from '@shared/components/add-form/add-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  StructureDialogComponent,
  StructureDialogData,
  StructureType,
  CapacityData,
} from '../components/structure-dialog/structure-dialog.component';
import { TooltipModule } from 'primeng/tooltip';
import { LogicalWarehouseService } from '../services/logical-warehouse.service';
import {
  VietnamAddressService,
  VietnamProvince,
  VietnamWard,
} from '../../services/vietnam-address.service';
import { CountriesService, Country } from '../../services/countries.service';
import { LogicalWarehouse } from '../models/logical-warehouse.model';
import {
  WarehouseDetailWithHierarchyDTO,
  ZoneDetailDTO,
  AisleDetailDTO,
  RackDetailDTO,
  ShelfDetailDTO,
  BinDetailDTO,
  WarehouseCreateWithHierarchyDTO,
  WarehouseUpdateWithHierarchyDTO,
  ZoneDTO,
  AisleDTO,
  RackDTO,
  ShelfDTO,
  BinDTO,
  UpdateZoneDTO,
  UpdateAisleDTO,
  UpdateRackDTO,
  UpdateShelfDTO,
  UpdateBinDTO,
} from '@app/api/stock-service';
import { vietnamesePhoneNumberValidator } from '@shared/utils/phone-validator.util';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { PermissionService } from '@app/core/services/permission.service';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import {
  findAllRacksInStructure,
  getAislesFromStructure,
  getRackDirectSlotsForStructureRow,
  getRackDirectSlotsFromStructure,
  getRacksFromStructure,
  getShelvesForStructureRow,
  getShelvesFromStructure,
  getSlotsForStructureRow,
  getSlotsFromStructure,
  hasStructureChildren,
} from './logical-warehouse-structure.util';

type Slot = {
  id: number;
  name: string;
  capacity?: CapacityData | null;
  description?: string;
  lengthM?: number | null;
  widthM?: number | null;
  heightM?: number | null;
};
type Shelf = {
  id: number;
  name: string;
  shelvesCapacity?: CapacityData | null;
  capacity?: CapacityData | null;
  description?: string;
  levelNo?: number | null;
  slots: Slot[];
};
type Rack = {
  id: number;
  name: string;
  capacity?: CapacityData | null;
  description?: string;
  maxLevelNo?: number | null;
  shelves: Shelf[];
  slots?: Slot[];
};
type Aisle = {
  id: number;
  name: string;
  description?: string;
  lengthM?: number | null;
  widthM?: number | null;
  racks: Rack[];
};
type ZoneNode = {
  id: number;
  name: string;
  description?: string;
  lengthM?: number | null;
  widthM?: number | null;
  aisles: Aisle[];
  racks?: Rack[];
};
type WarehouseStructure = {
  zones: ZoneNode[];
  aisles: Aisle[];
  racks: Rack[];
};
type StructureItem = {
  id: number;
  name: string;
  capacity?: CapacityData | null;
  dimensions?: { lengthM: number | null; widthM: number | null } | null;
  description?: string;
  maxLevelNo?: number | null;
  levelNo?: number | null;
};

@Component({
  selector: 'app-logical-warehouse-form',
  standalone: true,
  imports: [
    CommonModule,
    AddFormComponent,
    ToastModule,
    TranslateModule,
    ButtonModule,
    MenuModule,
    AppButtonComponent,
    StructureDialogComponent,
    TooltipModule,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './logical-warehouse-form.component.html',
  styleUrl: './logical-warehouse-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogicalWarehouseFormComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  readonly dimensionFractionDigits = 3;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly logicalWarehouseService = inject(LogicalWarehouseService);
  private readonly vietnamAddressService = inject(VietnamAddressService);
  private readonly countriesService = inject(CountriesService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'logical_warehouse' for warehouse permissions
  wms_approve = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.logical_warehouse.approve');
  });

  // Data signals
  loading = signal(false);
  submitting = signal(false);
  warehouseData = signal<LogicalWarehouse | null>(null);

  // Form configuration
  formConfig = signal({
    title: '',
    formFields: [] as FormField[],
    headerActions: [] as any[],
    sectionButtons: [] as any[],
    tableSections: [] as any[],
  });

  // Address data
  countries = signal<Country[]>([]);
  provinces = signal<VietnamProvince[]>([]);
  wards = signal<VietnamWard[]>([]);
  selectedProvinceId = signal<number | null>(null);

  // Zone options
  zoneOptions = signal<Array<{ label: string; value: number }>>([]);

  // Tab state
  activeTab = signal<'basic' | 'structure'>('basic');

  // Warehouse name from form
  warehouseName = signal<string>('');
  selectedWarehouse = signal<boolean>(false);

  // Warehouse structure stored locally (simulating API persistence)
  structureData = signal<WarehouseStructure>({
    zones: [],
    aisles: [],
    racks: [],
  });
  selectedZoneId = signal<number | null>(null);
  selectedAisleId = signal<number | null>(null);
  selectedRackId = signal<number | null>(null);
  selectedShelfId = signal<number | null>(null);
  selectedSlotId = signal<number | null>(null);

  // Multi-row selection state - track selected items per root item
  selectedItemsByRow = signal<
    Map<
      number,
      {
        zoneId?: number | null;
        aisleId?: number | null;
        rackId?: number | null;
        shelfId?: number | null;
        slotId?: number | null;
      }
    >
  >(new Map());
  structureMenuItems = signal<MenuItem[]>([]);
  showStructureDialog = signal(false);
  structureDialogType = signal<StructureType | null>(null);
  structureDialogMode = signal<'add' | 'edit'>('add');
  structureDialogInitialData = signal<StructureDialogData | null>(null);
  structureEditContext = signal<{
    type: StructureType;
    id: number;
    zoneId?: number;
    aisleId?: number;
    rackId?: number;
    shelfId?: number;
  } | null>(null);
  structureDialogParentOptions = signal<
    Array<{ label: string; value: number }>
  >([]);
  structureDialogParentLabel = signal<string>('');
  structureDialogZoneOptions = signal<Array<{ label: string; value: number }>>(
    [],
  );
  structureDialogAllAisleOptions = signal<
    Array<{ label: string; value: number; zoneId?: number | null }>
  >([]);

  showDeleteConfirmation = signal(false);
  deleteConfirmationTitle = signal('');
  deleteConfirmationMessage = signal('');
  pendingDelete: {
    type: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin';
    id: number;
  } | null = null;

  // Form component reference
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  // Track previous values
  private lastCountryId: string | null = null;
  private lastProvinceId: number | null = null;
  private skipCanDeactivate = false;

  // Store current form data temporarily (not in warehouseData to avoid triggering initialData setter)
  private currentFormData: any = null;

  // Computed values
  isEditMode = computed(() => {
    const url = this.router.url;
    return url.includes('/edit/');
  });
  isAddMode = computed(() => !this.isEditMode());

  /**
   * Show error using error dialog service with translation
   * Similar to batch-add.component.ts implementation
   */
  private showApiError(error: HttpErrorResponse, fallbackKey?: string): void {
    // Extract error message from API response
    // API response structure: { code: 400, message: "..." }
    const errorMessage = error?.error?.message || error?.message || '';

    // Use error translator service with optional fallback key
    const translatedMessage = fallbackKey
      ? this.errorTranslatorService.translate(errorMessage, fallbackKey)
      : this.errorTranslatorService.translate(errorMessage);

    this.errorDialogService.showError(translatedMessage);
  }

  ngOnInit(): void {
    this.initializeFormConfig();
    this.loadCountriesData();
    this.updateStructureMenuItems();

    if (this.isEditMode()) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.loadWarehouseData(+id);
      }
    } else {
      // Initialize empty structure for add mode
      this.structureData.set({ zones: [], aisles: [], racks: [] });
    }

    // Update form fields when language changes
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateFormFields();
        this.updateStructureMenuItems();
        this.cdr.markForCheck();
      });
  }

  setTab(tab: 'basic' | 'structure'): void {
    // Save current form data to warehouseData before switching tabs
    // This ensures form data is preserved when AddFormComponent is recreated
    if (this.activeTab() === 'basic' && this.addFormComponent) {
      // Get latest form data from AddFormComponent before switching
      let latestFormData = this.currentFormData;

      // Try to get form data directly from form if available
      const form = (this.addFormComponent as any)?.form;
      if (form && form.value) {
        latestFormData = form.value;
      } else if ((this.addFormComponent as any)?.getFormDataForSubmission) {
        latestFormData = (
          this.addFormComponent as any
        ).getFormDataForSubmission();
      }

      if (latestFormData) {
        const currentWarehouseData = this.warehouseData();
        this.warehouseData.set({
          ...(currentWarehouseData || {}),
          ...latestFormData,
        } as LogicalWarehouse);
      }
    } else if (this.currentFormData) {
      // If we have currentFormData, save it to warehouseData
      const currentWarehouseData = this.warehouseData();
      this.warehouseData.set({
        ...(currentWarehouseData || {}),
        ...this.currentFormData,
      } as LogicalWarehouse);
    }

    this.activeTab.set(tab);
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormConfig(): void {
    const title = this.isEditMode()
      ? this.translateService.instant('logical_warehouse.form.edit.title')
      : this.translateService.instant('logical_warehouse.form.add.title');

    const formFields = this.getFormFields();
    const headerActions = this.getHeaderActions();

    this.formConfig.set({
      title,
      formFields,
      headerActions,
      sectionButtons: [],
      tableSections: [],
    });
  }

  private loadCountriesData(): void {
    this.countriesService
      .loadCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (countries) => {
          this.countries.set(countries);
          this.updateFormFields();
        },
        error: (error) => {
          console.error('Error loading countries:', error);
        },
      });
  }

  private updateFormFields(): void {
    const currentConfig = this.formConfig();
    const formFields = this.getFormFields();

    this.formConfig.set({
      ...currentConfig,
      formFields,
    });
  }

  private getFormFields(): FormField[] {
    return [
      // Basic Information Section
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.name',
        ),
        controlName: 'name',
        type: 'text',
        required: true,
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_name',
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.code',
        ),
        controlName: 'code',
        type: 'text',
        required: true,
        disabled: this.isEditMode(),
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_code',
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.type_code',
        ),
        controlName: 'typeCode',
        type: 'dropdown',
        required: true,
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.select_type_code',
        ),
        options: [
          {
            label: 'logical_warehouse.form.type_codes.main',
            value: 'MAIN',
          },
          {
            label: 'logical_warehouse.form.type_codes.store',
            value: 'STORE',
          },
          {
            label: 'logical_warehouse.form.type_codes.cod',
            value: 'COD',
          },
        ],
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.contact_name',
        ),
        controlName: 'contactName',
        type: 'text',
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_contact_name',
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.contact_phone',
        ),
        controlName: 'contactPhone',
        type: 'text',
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_contact_phone',
        ),
        maxLength: 20,
        validators: [vietnamesePhoneNumberValidator],
        errorMessages: {
          invalidVietnamesePhone: this.translateService.instant(
            'common.validation.invalid_phone_format',
          ),
        },
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.description',
        ),
        controlName: 'description',
        type: 'text',
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_description',
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.length',
        ),
        controlName: 'length',
        type: 'number',
        required: true,
        decimal: true,
        useGrouping: false,
        maxFractionDigits: this.dimensionFractionDigits,
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_length',
        ),
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.width',
        ),
        controlName: 'width',
        type: 'number',
        required: true,
        decimal: true,
        useGrouping: false,
        maxFractionDigits: this.dimensionFractionDigits,
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_width',
        ),
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.height',
        ),
        controlName: 'height',
        type: 'number',
        required: true,
        decimal: true,
        useGrouping: false,
        maxFractionDigits: this.dimensionFractionDigits,
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_height',
        ),
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.actions.activate',
        ),
        controlName: 'status',
        type: 'checkbox',
        required: false,
        disabled: !this.wms_approve(),
        section: this.translateService.instant(
          'logical_warehouse.form.sections.basic_information',
        ),
      },
      // Address Information Section
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.country',
        ),
        controlName: 'countryId',
        type: 'dropdown',
        required: true,
        disabled: false,
        placeholder:
          this.countries().length > 0
            ? this.translateService.instant(
                'logical_warehouse.form.placeholders.select_country',
              )
            : this.translateService.instant(
                'logical_warehouse.form.placeholders.loading_countries',
              ),
        options: this.countries().map((c) => ({
          label:
            this.translateService.currentLang === 'vi'
              ? c.full_name
              : c.full_name_en,
          value: c.id,
        })),
        section: this.translateService.instant(
          'logical_warehouse.form.sections.address_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.province',
        ),
        controlName: 'provinceId',
        type: 'searchableDropdown',
        required: true,
        disabled: false,
        placeholder:
          this.provinces().length > 0
            ? this.translateService.instant(
                'logical_warehouse.form.placeholders.select_province',
              )
            : this.translateService.instant(
                'logical_warehouse.form.placeholders.no_results_found',
              ),
        options: this.provinces().map((p) => ({
          label:
            this.translateService.currentLang === 'vi'
              ? p.full_name
              : p.full_name_en,
          value: p.id,
        })),
        section: this.translateService.instant(
          'logical_warehouse.form.sections.address_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.ward_commune',
        ),
        controlName: 'cityId',
        type: 'searchableDropdown',
        required: true,
        disabled:
          !this.selectedProvinceId() ||
          !this.wards() ||
          this.wards().length === 0,
        placeholder: !this.selectedProvinceId()
          ? this.translateService.instant(
              'logical_warehouse.form.placeholders.select_province_first',
            )
          : this.wards() && this.wards().length > 0
            ? this.translateService.instant(
                'logical_warehouse.form.placeholders.select_ward_commune',
              )
            : this.translateService.instant(
                'logical_warehouse.form.placeholders.no_results_found',
              ),
        options: (this.wards() || []).map((w) => ({
          label:
            this.translateService.currentLang === 'vi'
              ? w.full_name
              : w.full_name_en,
          value: w.id,
        })),
        section: this.translateService.instant(
          'logical_warehouse.form.sections.address_information',
        ),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.fields.detailed_address',
        ),
        controlName: 'address',
        type: 'text',
        required: true,
        placeholder: this.translateService.instant(
          'logical_warehouse.form.placeholders.enter_detailed_address',
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'logical_warehouse.form.sections.address_information',
        ),
      },
    ];
  }

  getZones(): ZoneNode[] {
    return this.structureData().zones;
  }

  getRootAisles(): Aisle[] {
    return this.structureData().aisles;
  }

  getRootRacks(): Rack[] {
    return this.structureData().racks;
  }

  getAisles(): Aisle[] {
    return getAislesFromStructure(this.structureData(), this.selectedZoneId());
  }

  getRacks(): Rack[] {
    return getRacksFromStructure(
      this.structureData(),
      this.selectedZoneId(),
      this.selectedAisleId(),
    );
  }

  getShelves(): Shelf[] {
    return getShelvesFromStructure(this.structureData(), this.selectedRackId());
  }

  getSlots(): Slot[] {
    return getSlotsFromStructure(
      this.structureData(),
      this.selectedRackId(),
      this.selectedShelfId(),
    );
  }

  getRackDirectSlots(): Slot[] {
    return getRackDirectSlotsFromStructure(
      this.structureData(),
      this.selectedRackId(),
    );
  }

  // Computed signal to determine root-level structure rows
  // Includes zones (if any) and root-level aisles/racks
  rootStructureRows = computed(() => {
    const data = this.structureData();
    const rows: Array<{
      type: 'zone' | 'aisle' | 'rack';
      rootItem: ZoneNode | Aisle | Rack;
      rootItemId: number;
    }> = [];

    // Add zones to rows (each zone is a row)
    data.zones.forEach((zone) => {
      rows.push({ type: 'zone', rootItem: zone, rootItemId: zone.id });
    });

    // Add root-level aisles to rows
    data.aisles.forEach((aisle) => {
      rows.push({ type: 'aisle', rootItem: aisle, rootItemId: aisle.id });
    });

    // Add root-level racks to rows
    data.racks.forEach((rack) => {
      rows.push({ type: 'rack', rootItem: rack, rootItemId: rack.id });
    });

    return rows;
  });

  // Computed signal to determine sidebar chain for a root item
  getSidebarChainForRootItem(
    rootItem: ZoneNode | Aisle | Rack,
    type: 'zone' | 'aisle' | 'rack',
  ): Array<{
    type: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin';
    items: any[];
    selectedId: number | null;
  }> {
    const chain: Array<{
      type: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin';
      items: any[];
      selectedId: number | null;
    }> = [];

    const rowSelections = this.selectedItemsByRow().get(rootItem.id) || {};

    if (type === 'zone') {
      const zone = rootItem as ZoneNode;
      // Always show zone sidebar
      chain.push({
        type: 'zone',
        items: [zone],
        selectedId: rowSelections.zoneId || null,
      });

      // Only show sidebar when zone is selected
      if (rowSelections.zoneId === zone.id) {
        // Show aisle sidebar if there are aisles
        if (zone.aisles && zone.aisles.length > 0) {
          chain.push({
            type: 'aisle',
            items: zone.aisles,
            selectedId: rowSelections.aisleId || null,
          });

          // Only show rack sidebar when aisle is selected
          const selectedAisle = zone.aisles.find(
            (a) => a.id === rowSelections.aisleId,
          );
          if (
            selectedAisle &&
            selectedAisle.racks &&
            selectedAisle.racks.length > 0
          ) {
            chain.push({
              type: 'rack',
              items: selectedAisle.racks,
              selectedId: rowSelections.rackId || null,
            });
          }
        }

        // Show rack sidebar for racks directly in zone (if no aisle is selected or no aisles exist)
        if (zone.racks && zone.racks.length > 0 && !rowSelections.aisleId) {
          chain.push({
            type: 'rack',
            items: zone.racks,
            selectedId: rowSelections.rackId || null,
          });
        }
      }
    } else if (type === 'aisle') {
      const aisle = rootItem as Aisle;
      // Always show aisle sidebar
      chain.push({
        type: 'aisle',
        items: [aisle],
        selectedId: rowSelections.aisleId || null,
      });

      // Only show rack sidebar when aisle is selected
      if (
        rowSelections.aisleId === aisle.id &&
        aisle.racks &&
        aisle.racks.length > 0
      ) {
        chain.push({
          type: 'rack',
          items: aisle.racks,
          selectedId: rowSelections.rackId || null,
        });

        // Do not show shelf/bin in sidebar, only show in content area
      }
    } else if (type === 'rack') {
      const rack = rootItem as Rack;
      // Always show rack sidebar (rack is root item so always shown)
      chain.push({
        type: 'rack',
        items: [rack],
        selectedId: rowSelections.rackId || null,
      });

      // Do not show shelf/bin in sidebar, only show in content area
    }

    return chain;
  }

  // Helper method to automatically select parent item when adding children
  private autoSelectParentForNewChild(
    parentType: 'zone' | 'aisle' | 'rack' | 'shelf',
    parentId: number,
  ): void {
    // Find root item containing this parent
    const data = this.structureData();

    // Find zone containing parent
    for (const zone of data.zones) {
      if (parentType === 'zone' && zone.id === parentId) {
        const current = this.selectedItemsByRow();
        const rowSelections = current.get(zone.id) || {};
        rowSelections.zoneId = zone.id;
        this.selectedItemsByRow.set(
          new Map(current.set(zone.id, rowSelections)),
        );
        this.cdr.markForCheck();
        return;
      }

      // Find aisle in zone
      for (const aisle of zone.aisles) {
        if (parentType === 'aisle' && aisle.id === parentId) {
          const current = this.selectedItemsByRow();
          const rowSelections = current.get(zone.id) || {};
          rowSelections.zoneId = zone.id;
          rowSelections.aisleId = aisle.id;
          this.selectedItemsByRow.set(
            new Map(current.set(zone.id, rowSelections)),
          );
          this.cdr.markForCheck();
          return;
        }

        // Find rack in aisle
        for (const rack of aisle.racks) {
          if (parentType === 'rack' && rack.id === parentId) {
            const current = this.selectedItemsByRow();
            const rowSelections = current.get(zone.id) || {};
            rowSelections.zoneId = zone.id;
            rowSelections.aisleId = aisle.id;
            rowSelections.rackId = rack.id;
            this.selectedItemsByRow.set(
              new Map(current.set(zone.id, rowSelections)),
            );
            this.cdr.markForCheck();
            return;
          }
        }
      }

      // Find rack directly in zone
      if (zone.racks) {
        for (const rack of zone.racks) {
          if (parentType === 'rack' && rack.id === parentId) {
            const current = this.selectedItemsByRow();
            const rowSelections = current.get(zone.id) || {};
            rowSelections.zoneId = zone.id;
            rowSelections.rackId = rack.id;
            this.selectedItemsByRow.set(
              new Map(current.set(zone.id, rowSelections)),
            );
            this.cdr.markForCheck();
            return;
          }
        }
      }
    }

    // Find root-level aisle
    for (const aisle of data.aisles) {
      if (parentType === 'aisle' && aisle.id === parentId) {
        const current = this.selectedItemsByRow();
        const rowSelections = current.get(aisle.id) || {};
        rowSelections.aisleId = aisle.id;
        this.selectedItemsByRow.set(
          new Map(current.set(aisle.id, rowSelections)),
        );
        this.cdr.markForCheck();
        return;
      }

      // Find rack in root-level aisle
      for (const rack of aisle.racks) {
        if (parentType === 'rack' && rack.id === parentId) {
          const current = this.selectedItemsByRow();
          const rowSelections = current.get(aisle.id) || {};
          rowSelections.aisleId = aisle.id;
          rowSelections.rackId = rack.id;
          this.selectedItemsByRow.set(
            new Map(current.set(aisle.id, rowSelections)),
          );
          this.cdr.markForCheck();
          return;
        }
      }
    }

    // Find root-level rack
    for (const rack of data.racks) {
      if (parentType === 'rack' && rack.id === parentId) {
        const current = this.selectedItemsByRow();
        const rowSelections = current.get(rack.id) || {};
        rowSelections.rackId = rack.id;
        this.selectedItemsByRow.set(
          new Map(current.set(rack.id, rowSelections)),
        );
        this.cdr.markForCheck();
        return;
      }

      // Find shelf in rack
      if (rack.shelves) {
        for (const shelf of rack.shelves) {
          if (parentType === 'shelf' && shelf.id === parentId) {
            const current = this.selectedItemsByRow();
            const rowSelections = current.get(rack.id) || {};
            rowSelections.rackId = rack.id;
            rowSelections.shelfId = shelf.id;
            this.selectedItemsByRow.set(
              new Map(current.set(rack.id, rowSelections)),
            );
            this.cdr.markForCheck();
            return;
          }
        }
      }
    }

    // Find shelf in rack in zone
    for (const zone of data.zones) {
      if (zone.racks) {
        for (const rack of zone.racks) {
          if (rack.shelves) {
            for (const shelf of rack.shelves) {
              if (parentType === 'shelf' && shelf.id === parentId) {
                const current = this.selectedItemsByRow();
                const rowSelections = current.get(zone.id) || {};
                rowSelections.zoneId = zone.id;
                rowSelections.rackId = rack.id;
                rowSelections.shelfId = shelf.id;
                this.selectedItemsByRow.set(
                  new Map(current.set(zone.id, rowSelections)),
                );
                this.cdr.markForCheck();
                return;
              }
            }
          }
        }
      }

      for (const aisle of zone.aisles) {
        for (const rack of aisle.racks) {
          if (rack.shelves) {
            for (const shelf of rack.shelves) {
              if (parentType === 'shelf' && shelf.id === parentId) {
                const current = this.selectedItemsByRow();
                const rowSelections = current.get(zone.id) || {};
                rowSelections.zoneId = zone.id;
                rowSelections.aisleId = aisle.id;
                rowSelections.rackId = rack.id;
                rowSelections.shelfId = shelf.id;
                this.selectedItemsByRow.set(
                  new Map(current.set(zone.id, rowSelections)),
                );
                this.cdr.markForCheck();
                return;
              }
            }
          }
        }
      }
    }

    // Find shelf in rack in root-level aisle
    for (const aisle of data.aisles) {
      for (const rack of aisle.racks) {
        if (rack.shelves) {
          for (const shelf of rack.shelves) {
            if (parentType === 'shelf' && shelf.id === parentId) {
              const current = this.selectedItemsByRow();
              const rowSelections = current.get(aisle.id) || {};
              rowSelections.aisleId = aisle.id;
              rowSelections.rackId = rack.id;
              rowSelections.shelfId = shelf.id;
              this.selectedItemsByRow.set(
                new Map(current.set(aisle.id, rowSelections)),
              );
              this.cdr.markForCheck();
              return;
            }
          }
        }
      }
    }
  }

  // Helper method to check if item has children
  hasChildren(
    item: any,
    itemType: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin',
  ): boolean {
    return hasStructureChildren(item, itemType);
  }

  // Method to select item in a row
  onSelectItemInRow(
    row: {
      type: 'zone' | 'aisle' | 'rack';
      rootItem: ZoneNode | Aisle | Rack;
      rootItemId: number;
    },
    itemType: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin',
    itemId: number,
  ): void {
    const current = this.selectedItemsByRow();
    const rowSelections = current.get(row.rootItemId) || {};

    // Toggle selection
    if (itemType === 'zone') {
      rowSelections.zoneId = rowSelections.zoneId === itemId ? null : itemId;
      rowSelections.aisleId = null;
      rowSelections.rackId = null;
      rowSelections.shelfId = null;
      rowSelections.slotId = null;
    } else if (itemType === 'aisle') {
      rowSelections.aisleId = rowSelections.aisleId === itemId ? null : itemId;
      rowSelections.rackId = null;
      rowSelections.shelfId = null;
      rowSelections.slotId = null;
    } else if (itemType === 'rack') {
      rowSelections.rackId = rowSelections.rackId === itemId ? null : itemId;
      rowSelections.shelfId = null;
      rowSelections.slotId = null;
    } else if (itemType === 'shelf') {
      rowSelections.shelfId = rowSelections.shelfId === itemId ? null : itemId;
      rowSelections.slotId = null;
    } else if (itemType === 'bin') {
      rowSelections.slotId = rowSelections.slotId === itemId ? null : itemId;
    }

    this.selectedItemsByRow.set(
      new Map(current.set(row.rootItemId, rowSelections)),
    );

    // Update global selection for compatibility
    if (itemType === 'zone') {
      this.selectedZoneId.set(rowSelections.zoneId || null);
      this.selectedAisleId.set(null);
      this.selectedRackId.set(null);
      this.selectedShelfId.set(null);
      this.selectedSlotId.set(null);
    } else if (itemType === 'aisle') {
      this.selectedAisleId.set(rowSelections.aisleId || null);
      this.selectedRackId.set(null);
      this.selectedShelfId.set(null);
      this.selectedSlotId.set(null);
    } else if (itemType === 'rack') {
      this.selectedRackId.set(rowSelections.rackId || null);
      this.selectedShelfId.set(null);
      this.selectedSlotId.set(null);
    } else if (itemType === 'shelf') {
      this.selectedShelfId.set(rowSelections.shelfId || null);
      this.selectedSlotId.set(null);
    } else if (itemType === 'bin') {
      this.selectedSlotId.set(rowSelections.slotId || null);
    }

    this.cdr.markForCheck();
  }

  // Method to edit structure in a row
  onEditStructureInRow(
    event: Event,
    row: {
      type: 'zone' | 'aisle' | 'rack';
      rootItem: ZoneNode | Aisle | Rack;
      rootItemId: number;
    },
    itemType: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin',
    itemId: number,
  ): void {
    event.preventDefault();
    const rowSelections = this.selectedItemsByRow().get(row.rootItemId) || {};

    if (itemType === 'zone') {
      this.onEditStructure(event, 'zone', itemId);
    } else if (itemType === 'aisle') {
      this.onEditStructure(
        event,
        'aisle',
        itemId,
        rowSelections.zoneId || undefined,
      );
    } else if (itemType === 'rack') {
      this.onEditStructure(
        event,
        'rack',
        itemId,
        rowSelections.zoneId || undefined,
        rowSelections.aisleId || undefined,
      );
    } else if (itemType === 'shelf') {
      this.onEditStructure(
        event,
        'shelf',
        itemId,
        rowSelections.zoneId || undefined,
        rowSelections.aisleId || undefined,
        rowSelections.rackId || undefined,
      );
    } else if (itemType === 'bin') {
      const shelfCtx = this.findShelfContextForSlot(itemId);
      if (shelfCtx) {
        this.onEditBin(event, itemId, shelfCtx.shelfId);
      } else {
        const rackCtx = this.findRackContextForSlot(itemId);
        if (rackCtx) {
          this.onEditBinInRack(event, itemId, rackCtx.rackId);
        }
      }
    }
  }

  onDeleteStructureInRow(
    event: Event,
    row: {
      type: 'zone' | 'aisle' | 'rack';
      rootItem: ZoneNode | Aisle | Rack;
      rootItemId: number;
    },
    itemType: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin',
    itemId: number,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    this.pendingDelete = { type: itemType, id: itemId };
    const typeLabel = this.translateService.instant(
      `logical_warehouse.structure.${itemType}`,
    );
    const item = this.findItemById(itemType, itemId);
    const itemName = item?.name || '';
    this.deleteConfirmationTitle.set(
      this.translateService.instant('common.actions.delete') + ' ' + typeLabel,
    );
    this.deleteConfirmationMessage.set(
      this.translateService.instant('common.actions.confirm_proceed') +
        ' ' +
        this.translateService.instant('common.actions.delete') +
        ' ' +
        typeLabel +
        ' "' +
        itemName +
        '"' +
        '?',
    );
    this.showDeleteConfirmation.set(true);
  }

  onDeleteConfirmationAction(action: string): void {
    if (action === 'confirm' && this.pendingDelete) {
      this.deleteStructureNode(this.pendingDelete.type, this.pendingDelete.id);
    }
    this.showDeleteConfirmation.set(false);
    this.pendingDelete = null;
  }

  private findItemById(
    type: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin',
    id: number,
  ): any {
    if (type === 'zone') {
      return this.findAllZones().find((z) => z.id === id);
    } else if (type === 'aisle') {
      return this.findAllAisles().find((a) => a.id === id);
    } else if (type === 'rack') {
      return this.findAllRacks().find((r) => r.id === id);
    } else if (type === 'shelf') {
      return this.findAllShelves().find((s) => s.id === id);
    } else if (type === 'bin') {
      for (const shelf of this.findAllShelves()) {
        const bin = shelf.slots.find((s) => s.id === id);
        if (bin) return bin;
      }
      for (const rack of this.findAllRacks()) {
        const bin = rack.slots?.find((s) => s.id === id);
        if (bin) return bin;
      }
    }
    return null;
  }

  private deleteStructureNode(
    type: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin',
    id: number,
  ): void {
    const data = this.structureData();

    if (type === 'zone') {
      const updatedZones = data.zones.filter((z) => z.id !== id);
      this.structureData.set({ ...data, zones: updatedZones });
    } else if (type === 'aisle') {
      const updatedAisles = data.aisles.filter((a) => a.id !== id);
      const updatedZones = data.zones.map((zone) => ({
        ...zone,
        aisles: zone.aisles.filter((a) => a.id !== id),
      }));
      this.structureData.set({
        ...data,
        aisles: updatedAisles,
        zones: updatedZones,
      });
    } else if (type === 'rack') {
      const updatedRacks = data.racks.filter((r) => r.id !== id);
      const updatedAisles = data.aisles.map((aisle) => ({
        ...aisle,
        racks: aisle.racks.filter((r) => r.id !== id),
      }));
      const updatedZones = data.zones.map((zone) => ({
        ...zone,
        aisles: zone.aisles.map((aisle) => ({
          ...aisle,
          racks: aisle.racks.filter((r) => r.id !== id),
        })),
        racks: (zone.racks || []).filter((r) => r.id !== id),
      }));
      this.structureData.set({
        ...data,
        racks: updatedRacks,
        aisles: updatedAisles,
        zones: updatedZones,
      });
    } else if (type === 'shelf') {
      const shelfCtx = this.findShelfContext(id);
      if (!shelfCtx) return;

      const updateRacks = (racks: Rack[]) =>
        racks.map((r) =>
          r.id === shelfCtx.rackId
            ? { ...r, shelves: r.shelves.filter((s) => s.id !== id) }
            : r,
        );

      if (shelfCtx.zoneId === null && shelfCtx.aisleId === null) {
        this.structureData.set({
          ...data,
          racks: updateRacks(data.racks),
        });
      } else if (shelfCtx.zoneId === null && shelfCtx.aisleId !== null) {
        const updatedAisles = data.aisles.map((a) =>
          a.id === shelfCtx.aisleId ? { ...a, racks: updateRacks(a.racks) } : a,
        );
        this.structureData.set({ ...data, aisles: updatedAisles });
      } else if (shelfCtx.aisleId === null) {
        const updatedZones = data.zones.map((z) =>
          z.id === shelfCtx.zoneId
            ? { ...z, racks: updateRacks(z.racks || []) }
            : z,
        );
        this.structureData.set({ ...data, zones: updatedZones });
      } else {
        const updatedZones = data.zones.map((z) =>
          z.id === shelfCtx.zoneId
            ? {
                ...z,
                aisles: z.aisles.map((a) =>
                  a.id === shelfCtx.aisleId
                    ? { ...a, racks: updateRacks(a.racks) }
                    : a,
                ),
              }
            : z,
        );
        this.structureData.set({ ...data, zones: updatedZones });
      }
    } else if (type === 'bin') {
      const shelfCtx = this.findShelfContextForSlot(id);
      if (shelfCtx) {
        const updateShelves = (shelves: Shelf[]) =>
          shelves.map((s) =>
            s.id === shelfCtx.shelfId
              ? { ...s, slots: s.slots.filter((slot) => slot.id !== id) }
              : s,
          );
        const updateRacks = (racks: Rack[]) =>
          racks.map((r) =>
            r.id === shelfCtx.rackId
              ? { ...r, shelves: updateShelves(r.shelves) }
              : r,
          );

        if (shelfCtx.zoneId === null && shelfCtx.aisleId === null) {
          this.structureData.set({
            ...data,
            racks: updateRacks(data.racks),
          });
        } else if (shelfCtx.zoneId === null && shelfCtx.aisleId !== null) {
          const updatedAisles = data.aisles.map((a) =>
            a.id === shelfCtx.aisleId
              ? { ...a, racks: updateRacks(a.racks) }
              : a,
          );
          this.structureData.set({ ...data, aisles: updatedAisles });
        } else if (shelfCtx.aisleId === null) {
          const updatedZones = data.zones.map((z) =>
            z.id === shelfCtx.zoneId
              ? { ...z, racks: updateRacks(z.racks || []) }
              : z,
          );
          this.structureData.set({ ...data, zones: updatedZones });
        } else {
          const updatedZones = data.zones.map((z) =>
            z.id === shelfCtx.zoneId
              ? {
                  ...z,
                  aisles: z.aisles.map((a) =>
                    a.id === shelfCtx.aisleId
                      ? { ...a, racks: updateRacks(a.racks) }
                      : a,
                  ),
                }
              : z,
          );
          this.structureData.set({ ...data, zones: updatedZones });
        }
      } else {
        const rackCtx = this.findRackContextForSlot(id);
        if (!rackCtx) return;

        const updateRacks = (racks: Rack[]) =>
          racks.map((r) =>
            r.id === rackCtx.rackId
              ? {
                  ...r,
                  slots: (r.slots || []).filter((slot) => slot.id !== id),
                }
              : r,
          );

        if (rackCtx.zoneId === null && rackCtx.aisleId === null) {
          this.structureData.set({
            ...data,
            racks: updateRacks(data.racks),
          });
        } else if (rackCtx.zoneId === null && rackCtx.aisleId !== null) {
          const updatedAisles = data.aisles.map((a) =>
            a.id === rackCtx.aisleId
              ? { ...a, racks: updateRacks(a.racks) }
              : a,
          );
          this.structureData.set({ ...data, aisles: updatedAisles });
        } else if (rackCtx.aisleId === null) {
          const updatedZones = data.zones.map((z) =>
            z.id === rackCtx.zoneId
              ? { ...z, racks: updateRacks(z.racks || []) }
              : z,
          );
          this.structureData.set({ ...data, zones: updatedZones });
        } else {
          const updatedZones = data.zones.map((z) =>
            z.id === rackCtx.zoneId
              ? {
                  ...z,
                  aisles: z.aisles.map((a) =>
                    a.id === rackCtx.aisleId
                      ? { ...a, racks: updateRacks(a.racks) }
                      : a,
                  ),
                }
              : z,
          );
          this.structureData.set({ ...data, zones: updatedZones });
        }
      }
    }

    this.cdr.markForCheck();
  }

  // Method to get shelves for a row
  getShelvesForRow(row: {
    type: 'zone' | 'aisle' | 'rack';
    rootItem: ZoneNode | Aisle | Rack;
  }): Shelf[] {
    const rowSelections = this.selectedItemsByRow().get(row.rootItem.id) || {};
    return getShelvesForStructureRow(row as any, rowSelections);
  }

  // Method to get slots for a row (from shelf)
  getSlotsForRow(row: {
    type: 'zone' | 'aisle' | 'rack';
    rootItem: ZoneNode | Aisle | Rack;
  }): Slot[] {
    const rowSelections = this.selectedItemsByRow().get(row.rootItem.id) || {};
    return getSlotsForStructureRow(row as any, rowSelections);
  }

  // Method to get slots directly in rack for a row
  // Only return when rack is selected (active)
  getRackDirectSlotsForRow(row: {
    type: 'zone' | 'aisle' | 'rack';
    rootItem: ZoneNode | Aisle | Rack;
  }): Slot[] {
    const rowSelections = this.selectedItemsByRow().get(row.rootItem.id) || {};
    return getRackDirectSlotsForStructureRow(row as any, rowSelections);
  }

  // Flexible parent resolution helpers
  private findAllZones(): ZoneNode[] {
    return this.structureData().zones;
  }

  private findAllAisles(): Aisle[] {
    const aisles: Aisle[] = [...this.structureData().aisles];
    this.structureData().zones.forEach((z) => {
      if (z.aisles) {
        aisles.push(...z.aisles);
      }
    });
    return aisles;
  }

  private findAllAislesWithZoneInfo(): Array<{
    label: string;
    value: number;
    zoneId: number | null;
  }> {
    const result: Array<{
      label: string;
      value: number;
      zoneId: number | null;
    }> = [];
    // Root-level aisles
    this.structureData().aisles.forEach((a) => {
      result.push({ label: a.name, value: a.id, zoneId: null });
    });
    // Aisles in zones
    this.structureData().zones.forEach((z) => {
      if (z.aisles) {
        z.aisles.forEach((a) => {
          result.push({ label: a.name, value: a.id, zoneId: z.id });
        });
      }
    });
    return result;
  }

  private findAllRacks(): Rack[] {
    return findAllRacksInStructure(this.structureData());
  }

  private findAllShelves(): Shelf[] {
    const shelves: Shelf[] = [];
    this.findAllRacks().forEach((r) => {
      if (r.shelves) {
        shelves.push(...r.shelves);
      }
    });
    return shelves;
  }

  private findShelfContext(shelfId: number): {
    zoneId: number | null;
    aisleId: number | null;
    rackId: number;
    shelf: Shelf;
  } | null {
    // Check root-level racks
    for (const rack of this.structureData().racks) {
      const shelf = rack.shelves.find((s) => s.id === shelfId);
      if (shelf) {
        return { zoneId: null, aisleId: null, rackId: rack.id, shelf };
      }
    }
    // Check root-level aisles
    for (const aisle of this.structureData().aisles) {
      for (const rack of aisle.racks) {
        const shelf = rack.shelves.find((s) => s.id === shelfId);
        if (shelf) {
          return { zoneId: null, aisleId: aisle.id, rackId: rack.id, shelf };
        }
      }
    }
    // Check zones
    for (const zone of this.structureData().zones) {
      for (const aisle of zone.aisles) {
        for (const rack of aisle.racks) {
          const shelf = rack.shelves.find((s) => s.id === shelfId);
          if (shelf) {
            return {
              zoneId: zone.id,
              aisleId: aisle.id,
              rackId: rack.id,
              shelf,
            };
          }
        }
      }
      if (zone.racks) {
        for (const rack of zone.racks) {
          const shelf = rack.shelves.find((s) => s.id === shelfId);
          if (shelf) {
            return { zoneId: zone.id, aisleId: null, rackId: rack.id, shelf };
          }
        }
      }
    }
    return null;
  }

  private findRackContext(rackId: number): {
    zoneId: number | null;
    aisleId: number | null;
    rack: Rack;
  } | null {
    // Check root-level racks
    const rootRack = this.structureData().racks.find((r) => r.id === rackId);
    if (rootRack) {
      return { zoneId: null, aisleId: null, rack: rootRack };
    }
    // Check root-level aisles
    for (const aisle of this.structureData().aisles) {
      const rack = aisle.racks.find((r) => r.id === rackId);
      if (rack) {
        return { zoneId: null, aisleId: aisle.id, rack };
      }
    }
    // Check zones
    for (const zone of this.structureData().zones) {
      for (const aisle of zone.aisles) {
        const rack = aisle.racks.find((r) => r.id === rackId);
        if (rack) {
          return { zoneId: zone.id, aisleId: aisle.id, rack };
        }
      }
      if (zone.racks) {
        const rack = zone.racks.find((r) => r.id === rackId);
        if (rack) {
          return { zoneId: zone.id, aisleId: null, rack };
        }
      }
    }
    return null;
  }

  private findAisleContext(aisleId: number): {
    zoneId: number | null;
    aisle: Aisle;
  } | null {
    // Check root-level aisles
    const rootAisle = this.structureData().aisles.find((a) => a.id === aisleId);
    if (rootAisle) {
      return { zoneId: null, aisle: rootAisle };
    }
    // Check zones
    for (const zone of this.structureData().zones) {
      const aisle = zone.aisles.find((a) => a.id === aisleId);
      if (aisle) {
        return { zoneId: zone.id, aisle };
      }
    }
    return null;
  }

  private findShelfContextForSlot(slotId: number): {
    zoneId: number | null;
    aisleId: number | null;
    rackId: number;
    shelfId: number;
  } | null {
    for (const shelf of this.findAllShelves()) {
      const slot = shelf.slots.find((s) => s.id === slotId);
      if (slot) {
        const shelfCtx = this.findShelfContext(shelf.id);
        if (shelfCtx) {
          return {
            zoneId: shelfCtx.zoneId,
            aisleId: shelfCtx.aisleId,
            rackId: shelfCtx.rackId,
            shelfId: shelf.id,
          };
        }
      }
    }
    return null;
  }

  private findRackContextForSlot(slotId: number): {
    zoneId: number | null;
    aisleId: number | null;
    rackId: number;
  } | null {
    for (const rack of this.findAllRacks()) {
      const slot = rack.slots?.find((s) => s.id === slotId);
      if (slot) {
        const rackCtx = this.findRackContext(rack.id);
        if (rackCtx) {
          return {
            zoneId: rackCtx.zoneId,
            aisleId: rackCtx.aisleId,
            rackId: rack.id,
          };
        }
      }
    }
    return null;
  }

  getSelectedShelfName(): string {
    const shelf = this.getShelves().find(
      (s) => s.id === this.selectedShelfId(),
    );
    return shelf?.name || '-';
  }

  onSelectWarehouse(): void {
    this.selectedWarehouse.set(true);
    this.selectedZoneId.set(null);
    this.selectedAisleId.set(null);
    this.selectedRackId.set(null);
    this.selectedShelfId.set(null);
    this.selectedSlotId.set(null);
    this.cdr.markForCheck();
  }

  onSelectZone(id: number): void {
    if (this.selectedZoneId() === id) {
      // Toggle off - deselect zone
      this.selectedZoneId.set(null);
    } else {
      this.selectedZoneId.set(id);
    }
    this.selectedAisleId.set(null);
    this.selectedRackId.set(null);
    this.selectedShelfId.set(null);
    this.selectedSlotId.set(null);
    this.cdr.markForCheck();
  }

  onSelectAisle(id: number): void {
    if (this.selectedAisleId() === id) {
      // Toggle off - deselect aisle
      this.selectedAisleId.set(null);
    } else {
      this.selectedAisleId.set(id);
    }
    this.selectedRackId.set(null);
    this.selectedShelfId.set(null);
    this.selectedSlotId.set(null);
    this.cdr.markForCheck();
  }

  onSelectRack(id: number): void {
    if (this.selectedRackId() === id) {
      // Toggle off - deselect rack
      this.selectedRackId.set(null);
    } else {
      this.selectedRackId.set(id);
    }
    this.selectedShelfId.set(null);
    this.selectedSlotId.set(null);
    this.cdr.markForCheck();
  }

  onSelectShelf(id: number): void {
    if (this.selectedShelfId() === id) {
      // Toggle off - deselect shelf
      this.selectedShelfId.set(null);
    } else {
      this.selectedShelfId.set(id);
    }
    this.selectedSlotId.set(null);
    this.cdr.markForCheck();
  }

  onSelectSlot(id: number): void {
    if (this.selectedSlotId() === id) {
      // Toggle off - deselect slot
      this.selectedSlotId.set(null);
    } else {
      this.selectedSlotId.set(id);
    }
    this.cdr.markForCheck();
  }

  private updateStructureMenuItems(): void {
    this.structureMenuItems.set([
      {
        label: this.translateService.instant(
          'logical_warehouse.structure.zone',
        ),
        command: () => this.onAddStructure('zone'),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.structure.aisle',
        ),
        command: () => this.onAddStructure('aisle'),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.structure.rack',
        ),
        command: () => this.onAddStructure('rack'),
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.structure.shelf',
        ),
        command: () => this.onAddStructure('shelf'),
      },
      {
        label: this.translateService.instant('logical_warehouse.structure.bin'),
        command: () => this.onAddStructure('bin'),
      },
    ]);
  }

  onAddStructure(type: StructureType, skipParent: boolean = false): void {
    // If adding shelf but no rack exists, warn and skip dialog
    if (type === 'shelf' && this.findAllRacks().length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'logical_warehouse.messages.validation.create_rack_before_shelf',
        ),
        life: 3000,
      });
      return;
    }
    // If adding bin but no rack/shelf exists, warn and skip dialog
    if (
      type === 'bin' &&
      !skipParent &&
      this.findAllShelves().length === 0 &&
      this.findAllRacks().length === 0
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'logical_warehouse.messages.validation.select_parent_first',
        ),
        life: 3000,
      });
      return;
    }
    this.structureDialogType.set(type);
    this.structureDialogMode.set('add');
    this.structureEditContext.set(null);
    if (type === 'bin' && skipParent && this.selectedShelfId()) {
      // When adding bin from button, use selected shelf as parent and skip parent dropdown
      this.structureDialogInitialData.set({
        name: '',
        description: '',
        capacity: null,
        parentId: this.selectedShelfId()!,
      });
      this.structureDialogParentOptions.set([]);
      this.structureDialogParentLabel.set('');
    } else {
      this.structureDialogInitialData.set(null);
      this.setupParentOptionsForDialog(type, skipParent);
    }
    this.showStructureDialog.set(true);
    this.cdr.markForCheck();
  }

  onAddBinFromButton(): void {
    if (!this.selectedShelfId()) {
      return;
    }
    this.onAddStructure('bin', true);
  }

  onAddBinToRackFromButton(row: {
    type: 'zone' | 'aisle' | 'rack';
    rootItem: ZoneNode | Aisle | Rack;
    rootItemId: number;
  }): void {
    const rowSelections = this.selectedItemsByRow().get(row.rootItemId) || {};
    const rackId = rowSelections.rackId;
    if (!rackId) {
      return;
    }
    this.structureDialogType.set('bin');
    this.structureDialogMode.set('add');
    this.structureDialogInitialData.set({
      name: '',
      description: '',
      capacity: null,
      parentId: rackId,
    });
    this.setupParentOptionsForDialog('bin', false);
    this.showStructureDialog.set(true);
    this.cdr.markForCheck();
  }

  private setupParentOptionsForDialog(
    type: StructureType,
    skipParent: boolean = false,
    zoneId?: number,
    aisleId?: number,
    rackId?: number,
  ): void {
    if (type === 'zone') {
      this.structureDialogParentOptions.set([]);
      this.structureDialogParentLabel.set('');
      return;
    }

    if (type === 'aisle') {
      const options = this.findAllZones().map((z) => ({
        label: z.name,
        value: z.id,
      }));
      this.structureDialogParentOptions.set(options);
      this.structureDialogParentLabel.set(
        this.translateService.instant('logical_warehouse.structure.zone'),
      );
      return;
    }

    if (type === 'rack') {
      // Set zone options
      const zones = this.findAllZones();
      this.structureDialogZoneOptions.set(
        zones.map((z) => ({ label: z.name, value: z.id })),
      );

      // Set all aisle options with zoneId
      const allAisles = this.findAllAislesWithZoneInfo();
      this.structureDialogAllAisleOptions.set(allAisles);

      // Clear parent options (not used for rack now)
      this.structureDialogParentOptions.set([]);
      this.structureDialogParentLabel.set('');
      return;
    }

    if (type === 'shelf') {
      const racks = this.findAllRacks();
      const options = racks.map((r) => ({ label: r.name, value: r.id }));
      this.structureDialogParentOptions.set(options);
      this.structureDialogParentLabel.set(
        this.translateService.instant('logical_warehouse.structure.rack'),
      );
      return;
    }

    if (type === 'bin') {
      if (skipParent) {
        // Skip parent dropdown when adding from button
        this.structureDialogParentOptions.set([]);
        this.structureDialogParentLabel.set('');
        return;
      }
      const shelves = this.findAllShelves();
      const racks = this.findAllRacks();
      const options: Array<{ label: string; value: number }> = [];
      if (shelves.length > 0) {
        options.push(...shelves.map((s) => ({ label: s.name, value: s.id })));
      }
      if (racks.length > 0) {
        options.push(...racks.map((r) => ({ label: r.name, value: r.id })));
      }
      this.structureDialogParentLabel.set(
        this.translateService.instant('logical_warehouse.structure.rack'),
      );
      this.structureDialogParentOptions.set(options);
      return;
    }
  }
  onEditStructure(
    event: Event,
    type: StructureType,
    id: number,
    zoneId?: number | null,
    aisleId?: number | null,
    rackId?: number | null,
  ): void {
    event.preventDefault();
    const initialData = this.getStructureDialogInitialData(
      type,
      id,
      zoneId ?? undefined,
      aisleId ?? undefined,
      rackId ?? undefined,
    );
    if (!initialData) {
      return;
    }
    this.structureDialogType.set(type);
    this.structureDialogMode.set('edit');
    this.structureEditContext.set({
      type,
      id,
      zoneId: zoneId ?? undefined,
      aisleId: aisleId ?? undefined,
      rackId: rackId ?? undefined,
    });
    this.structureDialogInitialData.set(initialData);
    this.setupParentOptionsForDialog(
      type,
      false,
      zoneId ?? undefined,
      aisleId ?? undefined,
      rackId ?? undefined,
    );
    this.showStructureDialog.set(true);
    this.cdr.markForCheck();
  }

  onEditBin(event: Event, slotId: number, shelfId: number): void {
    event.preventDefault();
    const shelfCtx = this.findShelfContext(shelfId);
    if (!shelfCtx) {
      return;
    }
    const bin = shelfCtx.shelf.slots.find((s) => s.id === slotId);
    if (!bin) {
      return;
    }
    const initialData: StructureDialogData = {
      name: bin.name,
      capacity: (bin as any).capacity,
      description: (bin as any).description,
      parentId: shelfCtx.rackId,
    };
    this.structureDialogType.set('bin');
    this.structureDialogMode.set('edit');
    this.structureEditContext.set({
      type: 'bin',
      id: slotId,
      zoneId: shelfCtx.zoneId ?? undefined,
      aisleId: shelfCtx.aisleId ?? undefined,
      rackId: shelfCtx.rackId,
      shelfId,
    });
    this.structureDialogInitialData.set(initialData);
    this.setupParentOptionsForDialog('bin', false);
    this.showStructureDialog.set(true);
    this.cdr.markForCheck();
  }

  onEditBinInRack(event: Event, slotId: number, rackId?: number): void {
    event.preventDefault();
    const targetRackId = rackId || this.selectedRackId();
    if (!targetRackId) {
      return;
    }
    const rackCtx = this.findRackContext(targetRackId);
    if (!rackCtx) {
      return;
    }
    const bin = rackCtx.rack.slots?.find((s) => s.id === slotId);
    if (!bin) {
      return;
    }
    const initialData: StructureDialogData = {
      name: bin.name,
      capacity: (bin as any).capacity,
      description: (bin as any).description,
      parentId: targetRackId,
    };
    this.structureDialogType.set('bin');
    this.structureDialogMode.set('edit');
    this.structureEditContext.set({
      type: 'bin',
      id: slotId,
      zoneId: rackCtx.zoneId ?? undefined,
      aisleId: rackCtx.aisleId ?? undefined,
      rackId: targetRackId,
    });
    this.structureDialogInitialData.set(initialData);
    this.setupParentOptionsForDialog('bin', false);
    this.showStructureDialog.set(true);
    this.cdr.markForCheck();
  }

  onStructureDialogConfirm(data: StructureDialogData): void {
    const type = this.structureDialogType();
    if (!type) {
      this.showStructureDialog.set(false);
      return;
    }

    if (this.structureDialogMode() === 'edit' && this.structureEditContext()) {
      this.updateStructureNode(type, this.structureEditContext()!, data);
    } else {
      const item: StructureItem = {
        id: Date.now(),
        name: data.name,
        capacity: data.capacity,
        dimensions: data.dimensions ?? null,
        description: data.description,
        maxLevelNo: data.maxLevelNo ?? null,
        levelNo: data.levelNo ?? null,
      };
      if (type === 'rack') {
        this.addRackStructureNode(item, data.zoneId, data.aisleId);
      } else {
        this.addStructureNode(type, item, data.parentId ?? undefined);
      }
    }

    this.showStructureDialog.set(false);
    this.structureDialogMode.set('add');
    this.structureDialogInitialData.set(null);
    this.structureEditContext.set(null);
    this.cdr.markForCheck();
  }

  onStructureDialogVisibleChange(visible: boolean): void {
    this.showStructureDialog.set(visible);
    if (!visible) {
      this.structureDialogMode.set('add');
      this.structureDialogInitialData.set(null);
      this.structureEditContext.set(null);
    }
  }

  /**
   * Map API structure (WarehouseDetailWithHierarchyDTO) to internal format (WarehouseStructure)
   */
  private mapApiStructureToInternal(
    apiData: WarehouseDetailWithHierarchyDTO,
  ): void {
    const zones: ZoneNode[] = (apiData.zones || []).map((zone: ZoneDetailDTO) =>
      this.mapZoneDetailToZoneNode(zone),
    );

    const aisles: Aisle[] = (apiData.aisles || []).map(
      (aisle: AisleDetailDTO) => this.mapAisleDetailToAisle(aisle),
    );

    const racks: Rack[] = (apiData.racks || []).map((rack: RackDetailDTO) =>
      this.mapRackDetailToRack(rack),
    );

    this.structureData.set({ zones, aisles, racks });
    this.cdr.markForCheck();
  }

  /**
   * Map ZoneDetailDTO to ZoneNode
   */
  private mapZoneDetailToZoneNode(zone: ZoneDetailDTO): ZoneNode {
    return {
      id: zone.id || Date.now(),
      name: zone.name || '',
      description: zone.description,
      lengthM: zone.length ? zone.length / 100 : null, // Convert cm to m
      widthM: zone.width ? zone.width / 100 : null, // Convert cm to m
      aisles: (zone.aisles || []).map((aisle) =>
        this.mapAisleDetailToAisle(aisle),
      ),
      racks: (zone.racks || []).map((rack) => this.mapRackDetailToRack(rack)),
    };
  }

  /**
   * Map AisleDetailDTO to Aisle
   */
  private mapAisleDetailToAisle(aisle: AisleDetailDTO): Aisle {
    return {
      id: aisle.id || Date.now(),
      name: aisle.name || '',
      description: aisle.description,
      lengthM: aisle.length ? aisle.length / 100 : null, // Convert cm to m
      widthM: aisle.width ? aisle.width / 100 : null, // Convert cm to m
      racks: (aisle.racks || []).map((rack) => this.mapRackDetailToRack(rack)),
    };
  }

  /**
   * Map RackDetailDTO to Rack
   */
  private mapRackDetailToRack(rack: RackDetailDTO): Rack {
    return {
      id: rack.id || Date.now(),
      name: rack.name || '',
      description: rack.description,
      capacity: {
        weightKg: rack.weight ? rack.weight / 1000 : null, // Convert g to kg
        lengthM: rack.length ? rack.length / 100 : null, // Convert cm to m
        widthM: rack.width ? rack.width / 100 : null, // Convert cm to m
        heightM: rack.height ? rack.height / 100 : null, // Convert cm to m
      },
      maxLevelNo: rack.maxLevelNo ?? null,
      shelves: (rack.shelves || []).map((shelf) =>
        this.mapShelfDetailToShelf(shelf),
      ),
      slots: (rack.bins || []).map((bin) => this.mapBinDetailToSlot(bin)),
    };
  }

  /**
   * Map ShelfDetailDTO to Shelf
   */
  private mapShelfDetailToShelf(shelf: ShelfDetailDTO): Shelf {
    return {
      id: shelf.id || Date.now(),
      name: shelf.name || '',
      description: shelf.description,
      capacity: {
        weightKg: shelf.weight ? shelf.weight / 1000 : null, // Convert g to kg
        lengthM: shelf.length ? shelf.length / 100 : null, // Convert cm to m
        widthM: shelf.width ? shelf.width / 100 : null, // Convert cm to m
        heightM: shelf.height ? shelf.height / 100 : null, // Convert cm to m
      },
      levelNo: shelf.levelNo ?? null,
      slots: (shelf.bins || []).map((bin) => this.mapBinDetailToSlot(bin)),
    };
  }

  /**
   * Map BinDetailDTO to Slot
   */
  private mapBinDetailToSlot(bin: BinDetailDTO): Slot {
    return {
      id: bin.id || Date.now(),
      name: bin.name || '',
      description: bin.description,
      capacity: {
        weightKg: null, // Bins don't have weight
        lengthM: bin.length ? bin.length / 100 : null, // Convert cm to m
        widthM: bin.width ? bin.width / 100 : null, // Convert cm to m
        heightM: bin.height ? bin.height / 100 : null, // Convert cm to m
      },
      lengthM: bin.length ? bin.length / 100 : null, // Convert cm to m
      widthM: bin.width ? bin.width / 100 : null, // Convert cm to m
      heightM: bin.height ? bin.height / 100 : null, // Convert cm to m
    };
  }

  /**
   * Map internal structure to API format for create
   * Note: When creating, we should not include id in payload
   */
  private mapInternalStructureToApiForCreate(): {
    zones?: ZoneDTO[];
    aisles?: AisleDTO[];
    racks?: RackDTO[];
  } {
    const data = this.structureData();
    return {
      zones: data.zones.map((zone) => this.mapZoneNodeToZoneDTO(zone)),
      aisles: data.aisles.map((aisle) => this.mapAisleToAisleDTO(aisle)),
      racks: data.racks.map((rack) => this.mapRackToRackDTO(rack)),
    };
  }

  /**
   * Remove id from object recursively (for create operations)
   */
  private removeIdFromObject(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.removeIdFromObject(item));
    }
    if (typeof obj === 'object') {
      const { id, ...rest } = obj;
      const result: any = {};
      for (const key in rest) {
        if (rest.hasOwnProperty(key)) {
          result[key] = this.removeIdFromObject(rest[key]);
        }
      }
      return result;
    }
    return obj;
  }

  /**
   * Check if id is a server-generated id (not a temporary id from Date.now())
   * Server ids are typically small positive integers, while Date.now() generates very large numbers
   */
  private isServerId(id: number | undefined | null): boolean {
    if (id === undefined || id === null) {
      return false;
    }
    // Server ids are typically small positive integers (e.g., 1, 2, 3...)
    // Date.now() generates numbers like 1735123456789 (13 digits)
    // We consider ids < 1000000000 (1 billion) as server ids
    return id > 0 && id < 1000000000;
  }

  /**
   * Map internal structure to API format for update
   */
  private mapInternalStructureToApiForUpdate(): {
    zones?: UpdateZoneDTO[];
    aisles?: UpdateAisleDTO[];
    racks?: UpdateRackDTO[];
  } {
    const data = this.structureData();
    return {
      zones: data.zones.map((zone) => this.mapZoneNodeToUpdateZoneDTO(zone)),
      aisles: data.aisles.map((aisle) => this.mapAisleToUpdateAisleDTO(aisle)),
      racks: data.racks.map((rack) => this.mapRackToUpdateRackDTO(rack)),
    };
  }

  /**
   * Map ZoneNode to ZoneDTO (for create)
   */
  private mapZoneNodeToZoneDTO(zone: ZoneNode): ZoneDTO {
    return {
      name: zone.name,
      description: zone.description,
      length: zone.lengthM ? Math.round(zone.lengthM * 100) : undefined, // Convert m to cm
      width: zone.widthM ? Math.round(zone.widthM * 100) : undefined, // Convert m to cm
      aisles: (zone.aisles || []).map((aisle) =>
        this.mapAisleToAisleDTO(aisle),
      ),
      racks: (zone.racks || []).map((rack) => this.mapRackToRackDTO(rack)),
    };
  }

  /**
   * Map ZoneNode to UpdateZoneDTO (for update)
   * Only include id if it's a server-generated id (not a temporary id from Date.now())
   */
  private mapZoneNodeToUpdateZoneDTO(zone: ZoneNode): UpdateZoneDTO {
    const result: UpdateZoneDTO = {
      name: zone.name,
      description: zone.description,
      length: zone.lengthM ? Math.round(zone.lengthM * 100) : undefined, // Convert m to cm
      width: zone.widthM ? Math.round(zone.widthM * 100) : undefined, // Convert m to cm
      aisles: (zone.aisles || []).map((aisle) =>
        this.mapAisleToUpdateAisleDTO(aisle),
      ),
      racks: (zone.racks || []).map((rack) =>
        this.mapRackToUpdateRackDTO(rack),
      ),
    };
    // Only include id if it's a server-generated id
    if (this.isServerId(zone.id)) {
      result.id = zone.id;
    }
    return result;
  }

  /**
   * Map Aisle to AisleDTO (for create)
   */
  private mapAisleToAisleDTO(aisle: Aisle): AisleDTO {
    return {
      name: aisle.name,
      description: aisle.description,
      length: aisle.lengthM ? Math.round(aisle.lengthM * 100) : undefined, // Convert m to cm
      width: aisle.widthM ? Math.round(aisle.widthM * 100) : undefined, // Convert m to cm
      racks: (aisle.racks || []).map((rack) => this.mapRackToRackDTO(rack)), // Required field, always return array
    };
  }

  /**
   * Map Aisle to UpdateAisleDTO (for update)
   * Only include id if it's a server-generated id (not a temporary id from Date.now())
   */
  private mapAisleToUpdateAisleDTO(aisle: Aisle): UpdateAisleDTO {
    const result: UpdateAisleDTO = {
      name: aisle.name,
      description: aisle.description,
      length: aisle.lengthM ? Math.round(aisle.lengthM * 100) : undefined, // Convert m to cm
      width: aisle.widthM ? Math.round(aisle.widthM * 100) : undefined, // Convert m to cm
      racks: (aisle.racks || []).map((rack) =>
        this.mapRackToUpdateRackDTO(rack),
      ),
    };
    // Only include id if it's a server-generated id
    if (this.isServerId(aisle.id)) {
      result.id = aisle.id;
    }
    return result;
  }

  /**
   * Map Rack to RackDTO (for create)
   */
  private mapRackToRackDTO(rack: Rack): RackDTO {
    return {
      name: rack.name,
      description: rack.description,
      weight: rack.capacity?.weightKg
        ? Math.round(rack.capacity.weightKg * 1000)
        : 0, // Convert kg to g
      length: rack.capacity?.lengthM
        ? Math.round(rack.capacity.lengthM * 100)
        : 0, // Convert m to cm
      width: rack.capacity?.widthM ? Math.round(rack.capacity.widthM * 100) : 0, // Convert m to cm
      height: rack.capacity?.heightM
        ? Math.round(rack.capacity.heightM * 100)
        : 0, // Convert m to cm
      maxLevelNo: rack.maxLevelNo ?? this.findMaxLevelNo(rack),
      shelves: (rack.shelves || []).map((shelf) =>
        this.mapShelfToShelfDTO(shelf),
      ),
      bins: (rack.slots || []).map((slot) => this.mapSlotToBinDTO(slot)),
    };
  }

  /**
   * Map Rack to UpdateRackDTO (for update)
   * Only include id if it's a server-generated id (not a temporary id from Date.now())
   */
  private mapRackToUpdateRackDTO(rack: Rack): UpdateRackDTO {
    const result: UpdateRackDTO = {
      name: rack.name,
      description: rack.description,
      weight: rack.capacity?.weightKg
        ? Math.round(rack.capacity.weightKg * 1000)
        : 0, // Convert kg to g
      length: rack.capacity?.lengthM
        ? Math.round(rack.capacity.lengthM * 100)
        : 0, // Convert m to cm
      width: rack.capacity?.widthM ? Math.round(rack.capacity.widthM * 100) : 0, // Convert m to cm
      height: rack.capacity?.heightM
        ? Math.round(rack.capacity.heightM * 100)
        : 0, // Convert m to cm
      maxLevelNo: rack.maxLevelNo ?? this.findMaxLevelNo(rack),
      shelves: (rack.shelves || []).map((shelf) =>
        this.mapShelfToUpdateShelfDTO(shelf),
      ),
      bins: (rack.slots || []).map((slot) => this.mapSlotToUpdateBinDTO(slot)),
    };
    // Only include id if it's a server-generated id
    if (this.isServerId(rack.id)) {
      result.id = rack.id;
    }
    return result;
  }

  /**
   * Map Shelf to ShelfDTO (for create)
   */
  private mapShelfToShelfDTO(shelf: Shelf): ShelfDTO {
    return {
      name: shelf.name,
      description: shelf.description,
      weight: shelf.capacity?.weightKg
        ? Math.round(shelf.capacity.weightKg * 1000)
        : 0, // Convert kg to g
      length: shelf.capacity?.lengthM
        ? Math.round(shelf.capacity.lengthM * 100)
        : 0, // Convert m to cm
      width: shelf.capacity?.widthM
        ? Math.round(shelf.capacity.widthM * 100)
        : 0, // Convert m to cm
      height: shelf.capacity?.heightM
        ? Math.round(shelf.capacity.heightM * 100)
        : 0, // Convert m to cm
      levelNo: shelf.levelNo ?? this.findShelfLevelNo(shelf),
      bins: (shelf.slots || []).map((slot) => this.mapSlotToBinDTO(slot)),
    };
  }

  /**
   * Map Shelf to UpdateShelfDTO (for update)
   * Only include id if it's a server-generated id (not a temporary id from Date.now())
   */
  private mapShelfToUpdateShelfDTO(shelf: Shelf): UpdateShelfDTO {
    const result: UpdateShelfDTO = {
      name: shelf.name,
      description: shelf.description,
      weight: shelf.capacity?.weightKg
        ? Math.round(shelf.capacity.weightKg * 1000)
        : 0, // Convert kg to g
      length: shelf.capacity?.lengthM
        ? Math.round(shelf.capacity.lengthM * 100)
        : 0, // Convert m to cm
      width: shelf.capacity?.widthM
        ? Math.round(shelf.capacity.widthM * 100)
        : 0, // Convert m to cm
      height: shelf.capacity?.heightM
        ? Math.round(shelf.capacity.heightM * 100)
        : 0, // Convert m to cm
      levelNo: shelf.levelNo ?? this.findShelfLevelNo(shelf),
      bins: (shelf.slots || []).map((slot) => this.mapSlotToUpdateBinDTO(slot)),
    };
    // Only include id if it's a server-generated id
    if (this.isServerId(shelf.id)) {
      result.id = shelf.id;
    }
    return result;
  }

  /**
   * Map Slot to BinDTO (for create)
   */
  private mapSlotToBinDTO(slot: Slot): BinDTO {
    const length = slot.capacity?.lengthM
      ? Math.round(slot.capacity.lengthM * 100)
      : slot.lengthM
        ? Math.round(slot.lengthM * 100)
        : 0;
    const width = slot.capacity?.widthM
      ? Math.round(slot.capacity.widthM * 100)
      : slot.widthM
        ? Math.round(slot.widthM * 100)
        : 0;
    const height = slot.capacity?.heightM
      ? Math.round(slot.capacity.heightM * 100)
      : slot.heightM
        ? Math.round(slot.heightM * 100)
        : 0;

    return {
      name: slot.name,
      description: slot.description,
      length,
      width,
      height,
      products: [],
    };
  }

  /**
   * Map Slot to UpdateBinDTO (for update)
   * Only include id if it's a server-generated id (not a temporary id from Date.now())
   */
  private mapSlotToUpdateBinDTO(slot: Slot): UpdateBinDTO {
    const length = slot.capacity?.lengthM
      ? Math.round(slot.capacity.lengthM * 100)
      : slot.lengthM
        ? Math.round(slot.lengthM * 100)
        : 0;
    const width = slot.capacity?.widthM
      ? Math.round(slot.capacity.widthM * 100)
      : slot.widthM
        ? Math.round(slot.widthM * 100)
        : 0;
    const height = slot.capacity?.heightM
      ? Math.round(slot.capacity.heightM * 100)
      : slot.heightM
        ? Math.round(slot.heightM * 100)
        : 0;

    const result: UpdateBinDTO = {
      name: slot.name,
      description: slot.description,
      length,
      width,
      height,
      products: [],
    };
    // Only include id if it's a server-generated id
    if (this.isServerId(slot.id)) {
      result.id = slot.id;
    }
    return result;
  }

  /**
   * Find max level number from shelves in a rack
   */
  private findMaxLevelNo(rack: Rack): number {
    if (!rack.shelves || rack.shelves.length === 0) {
      return 1; // Default to 1 if no shelves
    }
    // Find the highest levelNo from shelves
    const maxLevel = Math.max(
      ...rack.shelves.map((shelf) => {
        // Try to find levelNo from shelf name or use index
        const levelMatch = shelf.name.match(/\d+/);
        return levelMatch ? parseInt(levelMatch[0], 10) : 1;
      }),
    );
    return maxLevel > 0 ? maxLevel : 1;
  }

  /**
   * Find level number for a shelf
   */
  private findShelfLevelNo(shelf: Shelf): number {
    // Try to extract level number from shelf name
    const levelMatch = shelf.name.match(/\d+/);
    return levelMatch ? parseInt(levelMatch[0], 10) : 1;
  }

  tooltipText(item: any): string {
    const parts: string[] = [];
    if (item?.name) {
      parts.push(item.name);
    }

    // Priority: capacity > dimensions > direct properties (lengthM/widthM)
    // This prevents duplication of length, width, height

    if (item?.capacity) {
      // For items with capacity (rack, shelf, bin/slot)
      const cap = item.capacity;
      const capacityParts: string[] = [];

      if (cap.lengthM !== null && cap.lengthM !== undefined) {
        capacityParts.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_length',
          ) +
            ': ' +
            cap.lengthM +
            ' m',
        );
      }
      if (cap.widthM !== null && cap.widthM !== undefined) {
        capacityParts.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_width',
          ) +
            ': ' +
            cap.widthM +
            ' m',
        );
      }
      if (cap.heightM !== null && cap.heightM !== undefined) {
        capacityParts.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_height',
          ) +
            ': ' +
            cap.heightM +
            ' m',
        );
      }
      if (cap.weightKg !== null && cap.weightKg !== undefined) {
        capacityParts.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_weight',
          ) +
            ': ' +
            cap.weightKg +
            ' kg',
        );
      }

      if (capacityParts.length > 0) {
        parts.push(capacityParts.join(', '));
      }

      // Add maxLevelNo for rack
      if (item?.maxLevelNo !== null && item?.maxLevelNo !== undefined) {
        parts.push(
          this.translateService.instant(
            'logical_warehouse.structure.max_level_no',
          ) +
            ': ' +
            item.maxLevelNo,
        );
      }

      // Add levelNo for shelf
      if (item?.levelNo !== null && item?.levelNo !== undefined) {
        parts.push(
          this.translateService.instant(
            'logical_warehouse.structure.level_no',
          ) +
            ': ' +
            item.levelNo,
        );
      }
    } else if (item?.dimensions) {
      // For items with dimensions object (fallback)
      const dim = item.dimensions;
      const dims: string[] = [];
      if (dim.lengthM !== null && dim.lengthM !== undefined) {
        dims.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_length',
          ) +
            ': ' +
            dim.lengthM +
            ' m',
        );
      }
      if (dim.widthM !== null && dim.widthM !== undefined) {
        dims.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_width',
          ) +
            ': ' +
            dim.widthM +
            ' m',
        );
      }
      if (dims.length > 0) {
        parts.push(dims.join(', '));
      }
    } else if (
      !item?.capacity &&
      (item?.lengthM !== undefined ||
        item?.widthM !== undefined ||
        item?.heightM !== undefined)
    ) {
      // For zone and aisle with direct lengthM/widthM/heightM properties (only if no capacity)
      const dims: string[] = [];
      if (item.lengthM !== null && item.lengthM !== undefined) {
        dims.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_length',
          ) +
            ': ' +
            item.lengthM +
            ' m',
        );
      }
      if (item.widthM !== null && item.widthM !== undefined) {
        dims.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_width',
          ) +
            ': ' +
            item.widthM +
            ' m',
        );
      }
      if (item.heightM !== null && item.heightM !== undefined) {
        dims.push(
          this.translateService.instant(
            'logical_warehouse.structure.capacity_height',
          ) +
            ': ' +
            item.heightM +
            ' m',
        );
      }
      if (dims.length > 0) {
        parts.push(dims.join(', '));
      }
    }

    if (item?.description) {
      parts.push(
        this.translateService.instant(
          'logical_warehouse.structure.description',
        ) +
          ': ' +
          item.description,
      );
    }
    return parts.join(' | ');
  }

  private getStructureDialogInitialData(
    type: StructureType,
    id: number,
    zoneId?: number,
    aisleId?: number,
    rackId?: number,
  ): StructureDialogData | null {
    if (type === 'zone') {
      const zone = this.findAllZones().find((z) => z.id === id);
      return zone
        ? {
            name: zone.name,
            dimensions:
              zone.lengthM !== undefined
                ? { lengthM: zone.lengthM ?? null, widthM: zone.widthM ?? null }
                : undefined,
            description: zone.description,
          }
        : null;
    }

    if (type === 'aisle') {
      const aisle = this.findAllAisles().find((a) => a.id === id);
      if (!aisle) return null;
      const aisleCtx = this.findAisleContext(id);
      return {
        name: aisle.name,
        dimensions:
          aisle.lengthM !== undefined
            ? { lengthM: aisle.lengthM ?? null, widthM: aisle.widthM ?? null }
            : undefined,
        description: aisle.description,
        parentId: aisleCtx?.zoneId ?? null,
      };
    }

    if (type === 'rack') {
      const rack = this.findAllRacks().find((r) => r.id === id);
      if (!rack) return null;
      const rackCtx = this.findRackContext(id);
      return {
        name: rack.name,
        capacity: rack.capacity,
        description: rack.description,
        zoneId: rackCtx?.zoneId ?? null,
        aisleId: rackCtx?.aisleId ?? null,
        maxLevelNo: rack.maxLevelNo ?? null,
      };
    }

    if (type === 'shelf') {
      const shelf = this.findAllShelves().find((s) => s.id === id);
      if (!shelf) return null;
      const shelfCtx = this.findShelfContext(id);
      return {
        name: shelf.name,
        capacity: shelf.capacity,
        description: shelf.description,
        parentId: shelfCtx?.rackId ?? null,
        levelNo: shelf.levelNo ?? null,
      };
    }

    if (type === 'bin') {
      for (const shelf of this.findAllShelves()) {
        const bin = shelf.slots.find((s) => s.id === id);
        if (bin) {
          const shelfCtx = this.findShelfContext(shelf.id);
          return {
            name: bin.name,
            capacity: bin.capacity,
            description: bin.description,
            parentId: shelfCtx?.rackId ?? null,
          };
        }
      }
      for (const rack of this.findAllRacks()) {
        const bin = rack.slots?.find((s) => s.id === id);
        if (bin) {
          return {
            name: bin.name,
            capacity: bin.capacity,
            description: bin.description,
            parentId: rack.id,
          };
        }
      }
      return null;
    }

    return null;
  }

  private addStructureNode(
    type: StructureType,
    item: StructureItem,
    parentId?: number,
  ): void {
    const itemName = (item as any).name ?? '';
    const itemCapacity = (item as any).capacity;
    const itemDimensions = (item as any).dimensions;
    const data = this.structureData();

    if (type === 'zone') {
      const newZone: ZoneNode = {
        id: item.id,
        name: itemName,
        lengthM: itemDimensions?.lengthM ?? null,
        widthM: itemDimensions?.widthM ?? null,
        description: item.description,
        aisles: [],
      };
      this.structureData.set({ ...data, zones: [...data.zones, newZone] });
      return;
    }

    if (type === 'aisle') {
      const newAisle: Aisle = {
        id: item.id,
        name: itemName,
        lengthM: itemDimensions?.lengthM ?? null,
        widthM: itemDimensions?.widthM ?? null,
        description: item.description,
        racks: [],
      };

      if (parentId) {
        // Add to zone
        const updatedZones = data.zones.map((zone) =>
          zone.id === parentId
            ? { ...zone, aisles: [...zone.aisles, newAisle] }
            : zone,
        );
        this.structureData.set({ ...data, zones: updatedZones });
        // Auto-select parent zone
        this.autoSelectParentForNewChild('zone', parentId);
      } else {
        // Add to root level
        this.structureData.set({ ...data, aisles: [...data.aisles, newAisle] });
      }
      return;
    }

    if (type === 'rack') {
      const newRack: Rack = {
        id: item.id,
        name: itemName,
        capacity: itemCapacity,
        description: item.description,
        shelves: [],
      };

      if (parentId) {
        // Check if parent is an aisle
        const aisleCtx = this.findAisleContext(parentId);
        if (aisleCtx) {
          if (aisleCtx.zoneId) {
            // Aisle is in a zone
            const updatedZones = data.zones.map((zone) =>
              zone.id === aisleCtx.zoneId
                ? {
                    ...zone,
                    aisles: zone.aisles.map((aisle) =>
                      aisle.id === parentId
                        ? { ...aisle, racks: [...aisle.racks, newRack] }
                        : aisle,
                    ),
                  }
                : zone,
            );
            this.structureData.set({ ...data, zones: updatedZones });
            // Auto-select parent aisle
            this.autoSelectParentForNewChild('aisle', parentId);
          } else {
            // Aisle is at root level
            const updatedAisles = data.aisles.map((aisle) =>
              aisle.id === parentId
                ? { ...aisle, racks: [...aisle.racks, newRack] }
                : aisle,
            );
            this.structureData.set({ ...data, aisles: updatedAisles });
            // Auto-select parent aisle
            this.autoSelectParentForNewChild('aisle', parentId);
          }
        } else {
          // Check if parent is a zone (direct rack under zone)
          const zone = data.zones.find((z) => z.id === parentId);
          if (zone) {
            const updatedZones = data.zones.map((z) =>
              z.id === parentId
                ? { ...z, racks: [...(z.racks || []), newRack] }
                : z,
            );
            this.structureData.set({ ...data, zones: updatedZones });
            // Auto-select parent zone
            this.autoSelectParentForNewChild('zone', parentId);
          }
        }
      } else {
        // Add to root level
        this.structureData.set({ ...data, racks: [...data.racks, newRack] });
      }
      return;
    }

    if (type === 'shelf') {
      if (!parentId) {
        return; // Shelf must have a rack parent
      }
      const newShelf: Shelf = {
        id: item.id,
        name: itemName,
        capacity: itemCapacity,
        description: item.description,
        levelNo: (item as any).levelNo ?? null,
        slots: [],
      };
      const rackCtx = this.findRackContext(parentId);
      if (!rackCtx) {
        return;
      }
      this.addShelfToRack(parentId, newShelf, rackCtx);
      // Auto-select parent rack
      this.autoSelectParentForNewChild('rack', parentId);
      return;
    }

    if (type === 'bin') {
      if (!parentId) {
        return; // Bin must have a shelf or rack parent
      }
      const newSlot: Slot = {
        id: item.id,
        name: itemName,
        capacity: itemCapacity,
        lengthM: itemDimensions?.lengthM ?? null,
        widthM: itemDimensions?.widthM ?? null,
        heightM: itemDimensions?.heightM ?? null,
        description: item.description,
      };
      // Try to add to shelf first
      const shelfCtx = this.findShelfContext(parentId);
      if (shelfCtx) {
        this.addSlotToShelf(parentId, newSlot, shelfCtx);
        // Auto-select parent shelf
        this.autoSelectParentForNewChild('shelf', parentId);
        return;
      }
      // If not a shelf, try to add directly to rack
      const rackCtx = this.findRackContext(parentId);
      if (rackCtx) {
        this.addSlotToRack(parentId, newSlot, rackCtx);
        // Auto-select parent rack
        this.autoSelectParentForNewChild('rack', parentId);
        return;
      }
    }
  }

  private addRackStructureNode(
    item: StructureItem,
    zoneId?: number | null,
    aisleId?: number | null,
  ): void {
    const itemName = (item as any).name ?? '';
    const itemCapacity = (item as any).capacity;
    const data = this.structureData();

    const newRack: Rack = {
      id: item.id,
      name: itemName,
      capacity: itemCapacity,
      description: item.description,
      maxLevelNo: (item as any).maxLevelNo ?? null,
      shelves: [],
    };

    if (aisleId) {
      // Add rack to aisle
      const aisleCtx = this.findAisleContext(aisleId);
      if (aisleCtx?.zoneId) {
        // Aisle is in a zone
        const updatedZones = data.zones.map((zone) =>
          zone.id === aisleCtx.zoneId
            ? {
                ...zone,
                aisles: zone.aisles.map((aisle) =>
                  aisle.id === aisleId
                    ? { ...aisle, racks: [...aisle.racks, newRack] }
                    : aisle,
                ),
              }
            : zone,
        );
        this.structureData.set({ ...data, zones: updatedZones });
        this.autoSelectParentForNewChild('aisle', aisleId);
      } else {
        // Aisle is at root level
        const updatedAisles = data.aisles.map((aisle) =>
          aisle.id === aisleId
            ? { ...aisle, racks: [...aisle.racks, newRack] }
            : aisle,
        );
        this.structureData.set({ ...data, aisles: updatedAisles });
        this.autoSelectParentForNewChild('aisle', aisleId);
      }
    } else if (zoneId) {
      // Add rack directly to zone (no aisle)
      const updatedZones = data.zones.map((z) =>
        z.id === zoneId ? { ...z, racks: [...(z.racks || []), newRack] } : z,
      );
      this.structureData.set({ ...data, zones: updatedZones });
      this.autoSelectParentForNewChild('zone', zoneId);
    } else {
      // Add to root level
      this.structureData.set({ ...data, racks: [...data.racks, newRack] });
      this.cdr.markForCheck();
    }
  }

  private addShelfToRack(
    rackId: number,
    shelf: Shelf,
    ctx: { zoneId: number | null; aisleId: number | null; rack: Rack },
  ): void {
    const data = this.structureData();
    if (ctx.zoneId === null && ctx.aisleId === null) {
      // Root-level rack
      const updatedRacks = data.racks.map((r) =>
        r.id === rackId ? { ...r, shelves: [...r.shelves, shelf] } : r,
      );
      this.structureData.set({ ...data, racks: updatedRacks });
    } else if (ctx.zoneId === null && ctx.aisleId !== null) {
      // Root-level aisle
      const updatedAisles = data.aisles.map((a) =>
        a.id === ctx.aisleId
          ? {
              ...a,
              racks: a.racks.map((r) =>
                r.id === rackId ? { ...r, shelves: [...r.shelves, shelf] } : r,
              ),
            }
          : a,
      );
      this.structureData.set({ ...data, aisles: updatedAisles });
    } else if (ctx.aisleId === null) {
      // Direct rack under zone
      const updatedZones = data.zones.map((z) =>
        z.id === ctx.zoneId
          ? {
              ...z,
              racks: (z.racks || []).map((r) =>
                r.id === rackId ? { ...r, shelves: [...r.shelves, shelf] } : r,
              ),
            }
          : z,
      );
      this.structureData.set({ ...data, zones: updatedZones });
    } else {
      // Rack under aisle in zone
      const updatedZones = data.zones.map((z) =>
        z.id === ctx.zoneId
          ? {
              ...z,
              aisles: z.aisles.map((a) =>
                a.id === ctx.aisleId
                  ? {
                      ...a,
                      racks: a.racks.map((r) =>
                        r.id === rackId
                          ? { ...r, shelves: [...r.shelves, shelf] }
                          : r,
                      ),
                    }
                  : a,
              ),
            }
          : z,
      );
      this.structureData.set({ ...data, zones: updatedZones });
    }
    // Auto-select parent rack
    this.autoSelectParentForNewChild('rack', rackId);
  }

  private addSlotToShelf(
    shelfId: number,
    slot: Slot,
    ctx: {
      zoneId: number | null;
      aisleId: number | null;
      rackId: number;
      shelf: Shelf;
    },
  ): void {
    const data = this.structureData();
    const updateShelves = (shelves: Shelf[]) =>
      shelves.map((s) =>
        s.id === shelfId ? { ...s, slots: [...s.slots, slot] } : s,
      );
    const updateRacks = (racks: Rack[]) =>
      racks.map((r) =>
        r.id === ctx.rackId ? { ...r, shelves: updateShelves(r.shelves) } : r,
      );

    if (ctx.zoneId === null && ctx.aisleId === null) {
      // Root-level rack
      this.structureData.set({ ...data, racks: updateRacks(data.racks) });
    } else if (ctx.zoneId === null && ctx.aisleId !== null) {
      // Root-level aisle
      const updatedAisles = data.aisles.map((a) =>
        a.id === ctx.aisleId ? { ...a, racks: updateRacks(a.racks) } : a,
      );
      this.structureData.set({ ...data, aisles: updatedAisles });
    } else if (ctx.aisleId === null) {
      // Direct rack under zone
      const updatedZones = data.zones.map((z) =>
        z.id === ctx.zoneId ? { ...z, racks: updateRacks(z.racks || []) } : z,
      );
      this.structureData.set({ ...data, zones: updatedZones });
    } else {
      // Rack under aisle in zone
      const updatedZones = data.zones.map((z) =>
        z.id === ctx.zoneId
          ? {
              ...z,
              aisles: z.aisles.map((a) =>
                a.id === ctx.aisleId
                  ? { ...a, racks: updateRacks(a.racks) }
                  : a,
              ),
            }
          : z,
      );
      this.structureData.set({ ...data, zones: updatedZones });
    }
    // Auto-select parent shelf
    this.autoSelectParentForNewChild('shelf', shelfId);
  }

  private addSlotToRack(
    rackId: number,
    slot: Slot,
    ctx: { zoneId: number | null; aisleId: number | null; rack: Rack },
  ): void {
    const data = this.structureData();
    const updateRacks = (racks: Rack[]) =>
      racks.map((r) =>
        r.id === rackId ? { ...r, slots: [...(r.slots || []), slot] } : r,
      );

    if (ctx.zoneId === null && ctx.aisleId === null) {
      // Root-level rack
      this.structureData.set({ ...data, racks: updateRacks(data.racks) });
    } else if (ctx.zoneId === null && ctx.aisleId !== null) {
      // Root-level aisle
      const updatedAisles = data.aisles.map((a) =>
        a.id === ctx.aisleId ? { ...a, racks: updateRacks(a.racks) } : a,
      );
      this.structureData.set({ ...data, aisles: updatedAisles });
    } else if (ctx.aisleId === null) {
      // Direct rack under zone
      const updatedZones = data.zones.map((z) =>
        z.id === ctx.zoneId ? { ...z, racks: updateRacks(z.racks || []) } : z,
      );
      this.structureData.set({ ...data, zones: updatedZones });
    } else {
      // Rack under aisle in zone
      const updatedZones = data.zones.map((z) =>
        z.id === ctx.zoneId
          ? {
              ...z,
              aisles: z.aisles.map((a) =>
                a.id === ctx.aisleId
                  ? { ...a, racks: updateRacks(a.racks) }
                  : a,
              ),
            }
          : z,
      );
      this.structureData.set({ ...data, zones: updatedZones });
    }
    // Auto-select parent rack
    this.autoSelectParentForNewChild('rack', rackId);
  }

  private updateStructureNode(
    type: StructureType,
    ctx: {
      type: StructureType;
      id: number;
      zoneId?: number;
      aisleId?: number;
      rackId?: number;
      shelfId?: number;
    },
    data: StructureDialogData,
  ): void {
    const name = data.name ?? '';
    const capacity = data.capacity;
    let structData = { ...this.structureData() };

    if (type === 'zone') {
      const updatedZones = structData.zones.map((z) =>
        z.id === ctx.id
          ? {
              ...z,
              name,
              lengthM: data.dimensions?.lengthM ?? z.lengthM ?? null,
              widthM: data.dimensions?.widthM ?? z.widthM ?? null,
              description: data.description,
            }
          : z,
      );
      this.structureData.set({ ...structData, zones: updatedZones });
      return;
    }

    if (type === 'aisle') {
      const updateAisle = (a: Aisle) =>
        a.id === ctx.id
          ? {
              ...a,
              name,
              lengthM: data.dimensions?.lengthM ?? a.lengthM ?? null,
              widthM: data.dimensions?.widthM ?? a.widthM ?? null,
              description: data.description,
            }
          : a;

      if (ctx.zoneId) {
        const updatedZones = structData.zones.map((z) =>
          z.id === ctx.zoneId ? { ...z, aisles: z.aisles.map(updateAisle) } : z,
        );
        this.structureData.set({ ...structData, zones: updatedZones });
      } else {
        const updatedAisles = structData.aisles.map(updateAisle);
        this.structureData.set({ ...structData, aisles: updatedAisles });
      }
      return;
    }

    if (type === 'rack') {
      const rackCtx = this.findRackContext(ctx.id);
      if (!rackCtx) return;

      const currentZoneId = rackCtx.zoneId;
      const currentAisleId = rackCtx.aisleId;
      const newZoneId = data.zoneId ?? null;
      const newAisleId = data.aisleId ?? null;

      const rack = rackCtx.rack;
      const updatedRack: Rack = {
        ...rack,
        name,
        capacity,
        description: data.description,
        maxLevelNo: data.maxLevelNo ?? rack.maxLevelNo ?? null,
      };

      const parentChanged =
        currentZoneId !== newZoneId || currentAisleId !== newAisleId;

      if (parentChanged) {
        let updatedStructData = { ...structData };

        if (currentZoneId === null && currentAisleId === null) {
          updatedStructData.racks = updatedStructData.racks.filter(
            (r) => r.id !== ctx.id,
          );
        } else if (currentZoneId === null && currentAisleId !== null) {
          updatedStructData.aisles = updatedStructData.aisles.map((a) =>
            a.id === currentAisleId
              ? { ...a, racks: a.racks.filter((r) => r.id !== ctx.id) }
              : a,
          );
        } else if (currentAisleId === null) {
          updatedStructData.zones = updatedStructData.zones.map((z) =>
            z.id === currentZoneId
              ? { ...z, racks: (z.racks || []).filter((r) => r.id !== ctx.id) }
              : z,
          );
        } else {
          updatedStructData.zones = updatedStructData.zones.map((z) =>
            z.id === currentZoneId
              ? {
                  ...z,
                  aisles: z.aisles.map((a) =>
                    a.id === currentAisleId
                      ? { ...a, racks: a.racks.filter((r) => r.id !== ctx.id) }
                      : a,
                  ),
                }
              : z,
          );
        }

        if (newAisleId) {
          const rootAisle = updatedStructData.aisles.find(
            (a) => a.id === newAisleId,
          );
          if (rootAisle) {
            updatedStructData.aisles = updatedStructData.aisles.map((a) =>
              a.id === newAisleId
                ? { ...a, racks: [...a.racks, updatedRack] }
                : a,
            );
          } else {
            for (const zone of updatedStructData.zones) {
              const aisle = zone.aisles.find((a) => a.id === newAisleId);
              if (aisle) {
                updatedStructData.zones = updatedStructData.zones.map((z) =>
                  z.id === zone.id
                    ? {
                        ...z,
                        aisles: z.aisles.map((a) =>
                          a.id === newAisleId
                            ? { ...a, racks: [...a.racks, updatedRack] }
                            : a,
                        ),
                      }
                    : z,
                );
                break;
              }
            }
          }
        } else if (newZoneId) {
          updatedStructData.zones = updatedStructData.zones.map((z) =>
            z.id === newZoneId
              ? { ...z, racks: [...(z.racks || []), updatedRack] }
              : z,
          );
        } else {
          updatedStructData.racks = [...updatedStructData.racks, updatedRack];
        }

        structData = { ...updatedStructData };
      } else {
        const updateRack = (r: Rack) => (r.id === ctx.id ? updatedRack : r);

        if (currentZoneId === null && currentAisleId === null) {
          structData.racks = structData.racks.map(updateRack);
        } else if (currentZoneId === null && currentAisleId !== null) {
          structData.aisles = structData.aisles.map((a) =>
            a.id === currentAisleId
              ? { ...a, racks: a.racks.map(updateRack) }
              : a,
          );
        } else if (currentAisleId === null) {
          structData.zones = structData.zones.map((z) =>
            z.id === currentZoneId
              ? { ...z, racks: (z.racks || []).map(updateRack) }
              : z,
          );
        } else {
          structData.zones = structData.zones.map((z) =>
            z.id === currentZoneId
              ? {
                  ...z,
                  aisles: z.aisles.map((a) =>
                    a.id === currentAisleId
                      ? { ...a, racks: a.racks.map(updateRack) }
                      : a,
                  ),
                }
              : z,
          );
        }
      }

      this.structureData.set(structData);

      if (parentChanged) {
        if (newAisleId) {
          const aisleCtx = this.findAisleContext(newAisleId);
          if (aisleCtx?.zoneId) {
            this.autoSelectParentForNewChild('aisle', newAisleId);
          } else {
            this.autoSelectParentForNewChild('aisle', newAisleId);
          }
        } else if (newZoneId) {
          this.autoSelectParentForNewChild('zone', newZoneId);
        }
      }

      this.cdr.markForCheck();
      return;
    }

    if (type === 'shelf' && ctx.rackId) {
      const updateShelf = (s: Shelf) =>
        s.id === ctx.id
          ? {
              ...s,
              name,
              capacity,
              description: data.description,
              levelNo: data.levelNo ?? s.levelNo ?? null,
            }
          : s;
      const updateRacks = (racks: Rack[]) =>
        racks.map((r) =>
          r.id === ctx.rackId
            ? { ...r, shelves: r.shelves.map(updateShelf) }
            : r,
        );

      const rackCtx = this.findRackContext(ctx.rackId);
      if (!rackCtx) return;

      if (rackCtx.zoneId === null && rackCtx.aisleId === null) {
        this.structureData.set({
          ...structData,
          racks: updateRacks(structData.racks),
        });
      } else if (rackCtx.zoneId === null && rackCtx.aisleId !== null) {
        const updatedAisles = structData.aisles.map((a) =>
          a.id === rackCtx.aisleId ? { ...a, racks: updateRacks(a.racks) } : a,
        );
        this.structureData.set({ ...structData, aisles: updatedAisles });
      } else if (rackCtx.aisleId === null) {
        const updatedZones = structData.zones.map((z) =>
          z.id === rackCtx.zoneId
            ? { ...z, racks: updateRacks(z.racks || []) }
            : z,
        );
        this.structureData.set({ ...structData, zones: updatedZones });
      } else {
        const updatedZones = structData.zones.map((z) =>
          z.id === rackCtx.zoneId
            ? {
                ...z,
                aisles: z.aisles.map((a) =>
                  a.id === rackCtx.aisleId
                    ? { ...a, racks: updateRacks(a.racks) }
                    : a,
                ),
              }
            : z,
        );
        this.structureData.set({ ...structData, zones: updatedZones });
      }
      return;
    }

    if (type === 'bin' && ctx.shelfId) {
      const shelfCtx = this.findShelfContext(ctx.shelfId);
      if (!shelfCtx) return;

      const updateSlot = (slot: Slot) =>
        slot.id === ctx.id
          ? { ...slot, name, capacity, description: data.description }
          : slot;
      const updateShelves = (shelves: Shelf[]) =>
        shelves.map((s) =>
          s.id === ctx.shelfId ? { ...s, slots: s.slots.map(updateSlot) } : s,
        );
      const updateRacks = (racks: Rack[]) =>
        racks.map((r) =>
          r.id === shelfCtx.rackId
            ? { ...r, shelves: updateShelves(r.shelves) }
            : r,
        );

      if (shelfCtx.zoneId === null && shelfCtx.aisleId === null) {
        this.structureData.set({
          ...structData,
          racks: updateRacks(structData.racks),
        });
      } else if (shelfCtx.zoneId === null && shelfCtx.aisleId !== null) {
        const updatedAisles = structData.aisles.map((a) =>
          a.id === shelfCtx.aisleId ? { ...a, racks: updateRacks(a.racks) } : a,
        );
        this.structureData.set({ ...structData, aisles: updatedAisles });
      } else if (shelfCtx.aisleId === null) {
        const updatedZones = structData.zones.map((z) =>
          z.id === shelfCtx.zoneId
            ? { ...z, racks: updateRacks(z.racks || []) }
            : z,
        );
        this.structureData.set({ ...structData, zones: updatedZones });
      } else {
        const updatedZones = structData.zones.map((z) =>
          z.id === shelfCtx.zoneId
            ? {
                ...z,
                aisles: z.aisles.map((a) =>
                  a.id === shelfCtx.aisleId
                    ? { ...a, racks: updateRacks(a.racks) }
                    : a,
                ),
              }
            : z,
        );
        this.structureData.set({ ...structData, zones: updatedZones });
      }
      return;
    }

    // Update bin directly in rack (no shelf)
    if (type === 'bin' && ctx.rackId && !ctx.shelfId) {
      const rackCtx = this.findRackContext(ctx.rackId);
      if (!rackCtx) return;

      const updateSlot = (slot: Slot) =>
        slot.id === ctx.id
          ? { ...slot, name, capacity, description: data.description }
          : slot;
      const updateRacks = (racks: Rack[]) =>
        racks.map((r) =>
          r.id === ctx.rackId
            ? { ...r, slots: (r.slots || []).map(updateSlot) }
            : r,
        );

      if (rackCtx.zoneId === null && rackCtx.aisleId === null) {
        this.structureData.set({
          ...structData,
          racks: updateRacks(structData.racks),
        });
      } else if (rackCtx.zoneId === null && rackCtx.aisleId !== null) {
        const updatedAisles = structData.aisles.map((a) =>
          a.id === rackCtx.aisleId ? { ...a, racks: updateRacks(a.racks) } : a,
        );
        this.structureData.set({ ...structData, aisles: updatedAisles });
      } else if (rackCtx.aisleId === null) {
        const updatedZones = structData.zones.map((z) =>
          z.id === rackCtx.zoneId
            ? { ...z, racks: updateRacks(z.racks || []) }
            : z,
        );
        this.structureData.set({ ...structData, zones: updatedZones });
      } else {
        const updatedZones = structData.zones.map((z) =>
          z.id === rackCtx.zoneId
            ? {
                ...z,
                aisles: z.aisles.map((a) =>
                  a.id === rackCtx.aisleId
                    ? { ...a, racks: updateRacks(a.racks) }
                    : a,
                ),
              }
            : z,
        );
        this.structureData.set({ ...structData, zones: updatedZones });
      }
    }
  }

  private getHeaderActions(): any[] {
    return [
      {
        label: this.translateService.instant(
          'logical_warehouse.form.actions.cancel',
        ),
        severity: 'secondary',
        outlined: true,
        action: 'cancel',
      },
      {
        label: this.translateService.instant(
          'logical_warehouse.form.actions.save',
        ),
        severity: 'info',
        action: 'save',
      },
    ];
  }

  private loadWarehouseData(id: number): void {
    this.loading.set(true);

    // Load warehouse with hierarchy
    this.logicalWarehouseService
      .getLogicalWarehouseWithHierarchyById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (warehouseWithHierarchy) => {
          // Map structure from API to internal format
          this.mapApiStructureToInternal(warehouseWithHierarchy);

          // Also load basic warehouse data for form fields
          this.logicalWarehouseService
            .getLogicalWarehouseById(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (warehouse) => {
                this.loadWarehouseFormData(warehouse);
              },
              error: (error: HttpErrorResponse) => {
                console.error('Error loading warehouse details:', error);
                this.showApiError(
                  error,
                  'logical_warehouse.messages.error.failed_to_load',
                );
                this.loading.set(false);
              },
            });
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error loading warehouse hierarchy:', error);
          // Fallback to basic warehouse data
          this.logicalWarehouseService
            .getLogicalWarehouseById(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (warehouse) => {
                this.loadWarehouseFormData(warehouse);
              },
              error: (err: HttpErrorResponse) => {
                this.showApiError(
                  err,
                  'logical_warehouse.messages.error.failed_to_load',
                );
                this.loading.set(false);
              },
            });
        },
      });
  }

  private loadWarehouseFormData(warehouse: LogicalWarehouse): void {
    // Map API data to form data
    // Note: API returns length, width, height in cm, weight in g
    // Form expects length, width, height in m, weight in kg
    const apiData = warehouse as any;
    const formData: any = {
      ...warehouse,
      countryId: 1, // Default to Vietnam
      provinceId: null,
      cityId: null, // cityId stores ward ID
      address: warehouse.detailAddress,
      length:
        apiData.length != null && apiData.length !== undefined
          ? apiData.length / 100
          : null, // Convert cm to m
      width:
        apiData.width != null && apiData.width !== undefined
          ? apiData.width / 100
          : null, // Convert cm to m
      height:
        apiData.height != null && apiData.height !== undefined
          ? apiData.height / 100
          : null, // Convert cm to m
      weight:
        apiData.weight != null && apiData.weight !== undefined
          ? apiData.weight / 1000
          : null, // Convert g to kg
    };

    // Load provinces for Vietnam (countryId = 1) and wait for them to load
    this.vietnamAddressService
      .loadProvinces()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (provinces) => {
          this.provinces.set(provinces);
          this.updateFormFields();

          // Find province by provinceCode
          // Note: API returns provinceCode as number (e.g., 75), which should match province.id in JSON
          if (warehouse.provinceCode) {
            const province = provinces.find(
              (p) =>
                p.id === warehouse.provinceCode ||
                Number(p.id) === Number(warehouse.provinceCode),
            );
            if (province) {
              formData.provinceId = province.id;
              this.selectedProvinceId.set(province.id);

              // Load wards for the selected province
              this.vietnamAddressService
                .loadWardsByProvince(province.id)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (wards) => {
                    this.wards.set(wards);
                    this.updateFormFields();

                    // Find ward by districtCode
                    // Note: API returns districtCode as number (e.g., 25195), which should match ward.id in JSON
                    if (warehouse.districtCode) {
                      const ward = wards.find(
                        (w) =>
                          w.id === warehouse.districtCode ||
                          Number(w.id) === Number(warehouse.districtCode),
                      );
                      if (ward) {
                        formData.cityId = ward.id; // cityId stores ward ID
                      } else {
                        // If ward not found in current province's wards, try to find it across all provinces
                        // This handles cases where the ward might be in a different province
                        this.vietnamAddressService
                          .findWardById(warehouse.districtCode)
                          .then((foundWard) => {
                            if (foundWard) {
                              // Update province if ward is in a different province
                              const correctProvince = provinces.find(
                                (p) => p.id === foundWard.province_id,
                              );
                              if (correctProvince) {
                                formData.provinceId = correctProvince.id;
                                this.selectedProvinceId.set(correctProvince.id);

                                // Reload wards for the correct province
                                this.vietnamAddressService
                                  .loadWardsByProvince(correctProvince.id)
                                  .pipe(takeUntil(this.destroy$))
                                  .subscribe({
                                    next: (correctWards) => {
                                      this.wards.set(correctWards);
                                      this.updateFormFields();
                                      formData.cityId = foundWard.id;
                                      this.setFormData(formData);
                                    },
                                    error: (error: unknown) => {
                                      console.error(
                                        'Error loading correct wards:',
                                        error,
                                      );
                                      this.setFormData(formData);
                                    },
                                  });
                              } else {
                                this.setFormData(formData);
                              }
                            } else {
                              this.setFormData(formData);
                            }
                          });
                        return; // Exit early, setFormData will be called in findWardById callback
                      }
                    }

                    // Set form data after wards are loaded
                    this.setFormData(formData);
                  },
                  error: (error: unknown) => {
                    console.error('Error loading wards:', error);
                    this.setFormData(formData);
                  },
                });
            } else {
              // Province not found, still set the data
              this.setFormData(formData);
            }
          } else {
            // No province code, set data as is
            this.setFormData(formData);
          }
        },
        error: (error: unknown) => {
          console.error('Error loading provinces:', error);
          this.setFormData(formData);
        },
      });
  }

  private setFormData(formData: any): void {
    // Update form fields first to ensure dropdown options are ready
    this.updateFormFields();

    // Set warehouse name for sidebar
    if (formData.name) {
      this.warehouseName.set(formData.name);
    }

    // Set data after ensuring form fields are updated
    // Use a delay to ensure dropdown options are populated
    setTimeout(() => {
      // Create a new object to trigger change detection
      const updatedFormData = { ...formData };

      // Set warehouseData - this will trigger initialData setter in AddFormComponent
      this.warehouseData.set(updatedFormData as LogicalWarehouse);
      this.cdr.markForCheck();

      // Additional delay to ensure form receives the update
      // Then manually update form control values if needed
      setTimeout(() => {
        // Try multiple times to ensure form is ready
        const tryUpdateForm = (attempts: number = 0) => {
          if (this.addFormComponent) {
            const form = (this.addFormComponent as any).form;
            if (form) {
              // Form is ready, update values directly if they exist
              const provinceControl = form.get('provinceId');
              const cityControl = form.get('cityId');
              const zoneIdsControl = form.get('zoneIds');

              // Ensure provinceId is set correctly
              if (
                provinceControl &&
                formData.provinceId !== null &&
                formData.provinceId !== undefined
              ) {
                const provinceValue = Number(formData.provinceId);
                provinceControl.setValue(provinceValue, { emitEvent: false });
              }

              // Ensure cityId (ward ID) is set correctly
              if (
                cityControl &&
                formData.cityId !== null &&
                formData.cityId !== undefined
              ) {
                const cityValue = Number(formData.cityId);
                cityControl.setValue(cityValue, { emitEvent: false });
              }

              // Force change detection
              this.cdr.detectChanges();

              // Set initialData again to ensure form receives the update
              setTimeout(() => {
                this.warehouseData.set({
                  ...updatedFormData,
                } as LogicalWarehouse);
                this.cdr.detectChanges();

                // Ensure zoneIds is set after form is fully initialized
                // Only set if zones are loaded (zoneOptions has data)
                if (
                  zoneIdsControl &&
                  formData.zoneIds &&
                  Array.isArray(formData.zoneIds) &&
                  formData.zoneIds.length > 0 &&
                  this.zoneOptions().length > 0
                ) {
                  // Set zoneIds again to ensure it's applied
                  zoneIdsControl.setValue(formData.zoneIds, {
                    emitEvent: false,
                  });
                  this.cdr.detectChanges();
                } else if (
                  formData.zoneIds &&
                  Array.isArray(formData.zoneIds) &&
                  formData.zoneIds.length > 0 &&
                  this.zoneOptions().length === 0
                ) {
                  // Zones not loaded yet, wait a bit and try again
                  setTimeout(() => {
                    if (zoneIdsControl && this.zoneOptions().length > 0) {
                      zoneIdsControl.setValue(formData.zoneIds, {
                        emitEvent: false,
                      });
                      this.cdr.detectChanges();
                    }
                  }, 300);
                }

                this.loading.set(false);
              }, 100);
              return;
            }
          }

          // Form not ready yet, try again
          if (attempts < 10) {
            setTimeout(() => tryUpdateForm(attempts + 1), 200);
          } else {
            // Max attempts reached, still set loading to false
            // Form will display with available data even if province/ward not found
            this.warehouseData.set({ ...updatedFormData } as LogicalWarehouse);
            this.cdr.detectChanges();
            this.loading.set(false);
          }
        };

        tryUpdateForm();
      }, 500);
    }, 500);
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'cancel':
        this.onCancel();
        break;
      case 'save':
        // Check if basic form data is null/empty
        const basicFormData =
          this.warehouseData() ||
          (this.addFormComponent as any)?.getFormDataForSubmission?.();
        const isBasicFormEmpty =
          !basicFormData ||
          !basicFormData.name ||
          !basicFormData.code ||
          !basicFormData.typeCode;

        // If we're on structure tab, save current form data before switching
        if (this.activeTab() !== 'basic' && this.addFormComponent) {
          const currentFormData = (
            this.addFormComponent as any
          )?.getFormDataForSubmission?.();
          if (currentFormData) {
            // Update warehouseData to preserve form data before switching tabs
            const currentWarehouseData = this.warehouseData();
            this.warehouseData.set({
              ...(currentWarehouseData || {}),
              ...currentFormData,
            } as LogicalWarehouse);
          }
        }

        // Only switch to basic tab if basic form is empty/null
        // If basic form has data and we're on structure tab, stay on structure tab
        if (isBasicFormEmpty && this.activeTab() !== 'basic') {
          this.setTab('basic');
          // Wait for form to render, then check validity and trigger validation if needed
          setTimeout(() => {
            if (this.addFormComponent?.form?.invalid) {
              // Trigger validation in AddFormComponent
              (this.addFormComponent as any).markFormGroupTouched?.();
              this.cdr.markForCheck();
            } else {
              // Form is valid, get form data from AddFormComponent or use saved data
              const formData =
                (this.addFormComponent as any)?.getFormDataForSubmission?.() ||
                this.warehouseData() ||
                event.data;
              if (formData) {
                this.onSubmit(formData);
              }
            }
          }, 100); // Increased timeout to ensure form is fully rendered
        } else if (this.activeTab() === 'basic') {
          // Already on basic tab, check form validity
          if (this.addFormComponent?.form?.invalid) {
            // Trigger validation in AddFormComponent
            (this.addFormComponent as any).markFormGroupTouched?.();
            this.cdr.markForCheck();
          } else {
            // Form is valid, get form data from AddFormComponent or use saved data
            const formData =
              (this.addFormComponent as any)?.getFormDataForSubmission?.() ||
              this.warehouseData() ||
              event.data;
            if (formData) {
              this.onSubmit(formData);
            }
          }
        } else {
          // We're on structure tab and basic form has data, submit directly
          const formData = this.warehouseData() || event.data;
          if (formData) {
            this.onSubmit(formData);
          }
        }
        break;
    }
  }

  onFormValueChanges(formData: any): void {
    const currentCountryId = formData.countryId;
    const currentProvinceId = formData.provinceId;

    // Track warehouse name
    if (formData.name) {
      this.warehouseName.set(formData.name);
    } else {
      this.warehouseName.set('');
    }

    // Store form data in a temporary variable (not in warehouseData to avoid triggering initialData setter)
    // This will be saved to warehouseData when switching tabs or saving
    this.currentFormData = formData;

    // Handle country change
    if (currentCountryId && currentCountryId !== this.lastCountryId) {
      this.lastCountryId = currentCountryId;
      this.onCountryChange(currentCountryId);
    }

    // Handle province change
    const provinceIdNum = currentProvinceId
      ? typeof currentProvinceId === 'string'
        ? parseInt(currentProvinceId, 10)
        : Number(currentProvinceId)
      : null;

    if (provinceIdNum && provinceIdNum !== this.lastProvinceId) {
      this.lastProvinceId = provinceIdNum;
      // Set selectedProvinceId first to ensure form field disabled state is updated
      this.selectedProvinceId.set(provinceIdNum);
      // Update form fields immediately to reflect the change
      this.updateFormFields();
      this.cdr.markForCheck();
      // Then load wards
      this.onProvinceChange(provinceIdNum);
    } else if (!provinceIdNum && this.lastProvinceId !== null) {
      this.lastProvinceId = null;
      this.selectedProvinceId.set(null);
      this.wards.set([]);
      this.updateFormFields();
      this.cdr.markForCheck();
    }
  }

  private onCountryChange(countryId: string | number): void {
    // Reset province and ward data immediately
    this.provinces.set([]);
    this.wards.set([]);
    this.selectedProvinceId.set(null);
    this.lastProvinceId = null;

    // Check if it's Vietnam (ID: 1 or "1" or "VN")
    if (countryId === 1 || countryId === '1' || countryId === 'VN') {
      // Load Vietnamese provinces for Vietnam
      this.vietnamAddressService
        .loadProvinces()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (provinces) => {
            this.provinces.set(provinces);
            this.updateFormFields();
          },
          error: (error: unknown) => {
            console.error('Error loading provinces:', error);
          },
        });
    } else {
      // For other countries, provinces and wards are already cleared above
      this.updateFormFields();
    }
  }

  private onProvinceChange(provinceId: number): void {
    // Reset ward data when province changes
    this.wards.set([]);
    // Update form fields immediately to disable cityId field
    this.updateFormFields();
    this.cdr.markForCheck();

    if (provinceId) {
      this.vietnamAddressService
        .loadWardsByProvince(provinceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (wards) => {
            this.wards.set(wards);
            // Update form fields after wards are loaded to enable cityId field
            // AddFormComponent will automatically update disabled states when formFields change
            this.updateFormFields();
            this.cdr.markForCheck();
          },
          error: (error: unknown) => {
            console.error('Error loading wards:', error);
            this.updateFormFields();
            this.cdr.markForCheck();
          },
        });
    } else {
      this.updateFormFields();
      this.cdr.markForCheck();
    }
  }

  onSubmit(formData?: any): void {
    if (!formData) {
      this.submitting.set(false);
      return;
    }

    this.submitting.set(true);

    // Validate required fields
    if (
      !formData.name ||
      !formData.code ||
      !formData.typeCode ||
      !formData.countryId ||
      !formData.provinceId ||
      !formData.cityId ||
      !formData.address
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'logical_warehouse.messages.error.title',
        ),
        detail: this.translateService.instant(
          'logical_warehouse.messages.validation.fill_required_fields',
        ),
        life: 6000,
      });
      this.submitting.set(false);
      return;
    }

    // Ensure provinces and wards are loaded
    if (this.provinces().length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'logical_warehouse.messages.error.title',
        ),
        detail: 'Vui lòng đợi danh sách tỉnh/thành phố được tải xong.',
        life: 6000,
      });
      this.submitting.set(false);
      return;
    }

    // Get province and ward from selected IDs
    // Handle both string and number types for IDs
    const provinceId =
      formData.provinceId !== null && formData.provinceId !== undefined
        ? typeof formData.provinceId === 'string'
          ? parseInt(formData.provinceId, 10)
          : Number(formData.provinceId)
        : null;
    const wardId =
      formData.cityId !== null && formData.cityId !== undefined
        ? typeof formData.cityId === 'string'
          ? parseInt(formData.cityId, 10)
          : Number(formData.cityId)
        : null;

    // Find province by ID
    const province = provinceId
      ? this.provinces().find((p) => {
          const pId = Number(p.id);
          const searchId = Number(provinceId);
          return pId === searchId;
        })
      : null;

    // Ensure wards are loaded for the selected province
    if (province && this.wards().length === 0 && wardId) {
      // Load wards for the province
      this.vietnamAddressService
        .loadWardsByProvince(province.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (wards) => {
            this.wards.set(wards);
            this.updateFormFields();
            // Retry submit after wards are loaded
            setTimeout(() => {
              this.onSubmit(formData);
            }, 100);
          },
          error: (error: HttpErrorResponse) => {
            console.error('Error loading wards:', error);
            this.showApiError(
              error,
              'logical_warehouse.messages.error.failed_to_load_wards',
            );
            this.submitting.set(false);
          },
        });
      return;
    }

    // Find ward by ID
    const ward = wardId
      ? this.wards().find((w) => {
          const wId = Number(w.id);
          const searchId = Number(wardId);
          return wId === searchId;
        })
      : null;

    // Determine provinceCode, cityCode, districtCode
    let provinceCode: number;
    let provinceName: string;
    let cityCode: number;
    let cityName: string;
    let districtCode: number;
    let districtName: string;

    // Map province to codes
    // Note: JSON has string code (e.g., "ha_noi"), but API needs number code
    // Use province.id as code since it's a number and matches API format
    if (province && province.id) {
      // Found province in JSON (user selected), use its id as code
      provinceCode = Number(province.id);
      provinceName = province.full_name_en || province.name || '';
      cityCode = provinceCode; // cityCode = provinceCode
      cityName = provinceName; // cityName = provinceName
    } else if (
      !provinceId &&
      this.isEditMode() &&
      this.warehouseData()?.provinceCode
    ) {
      // Edit mode, user didn't select new province, use code from API
      provinceCode = this.warehouseData()!.provinceCode!;
      provinceName = this.warehouseData()!.provinceName || '';
      cityCode = provinceCode; // cityCode = provinceCode
      cityName = provinceName; // cityName = provinceName
    } else {
      // Province selected but not found in JSON, or no province at all
      console.error('Province not found:', {
        provinceId,
        formDataProvinceId: formData.provinceId,
        provincesCount: this.provinces().length,
        provinces: this.provinces()
          .slice(0, 5)
          .map((p) => ({
            id: p.id,
            code: p.code,
            name: p.full_name_en,
          })),
      });
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'logical_warehouse.messages.error.title',
        ),
        detail: this.translateService.instant(
          'logical_warehouse.messages.error.invalid_province',
        ),
        life: 6000,
      });
      this.submitting.set(false);
      return;
    }

    // Map ward to codes
    // Note: JSON has string code (e.g., "ba_dinh"), but API needs number code
    // Use ward.id as code since it's a number and matches API format
    if (ward && ward.id) {
      // Found ward in JSON (user selected), use its id as code
      districtCode = Number(ward.id);
      districtName = ward.full_name_en || ward.name || '';
    } else if (
      !wardId &&
      this.isEditMode() &&
      this.warehouseData()?.districtCode
    ) {
      // Edit mode, user didn't select new ward, use code from API
      districtCode = this.warehouseData()!.districtCode!;
      districtName = this.warehouseData()!.districtName || '';
    } else {
      // Ward selected but not found in JSON, or no ward at all
      console.error('Ward not found:', {
        wardId,
        formDataCityId: formData.cityId,
        wardsCount: this.wards().length,
        wards: this.wards()
          .slice(0, 5)
          .map((w) => ({
            id: w.id,
            code: w.code,
            name: w.full_name_en,
          })),
      });
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'logical_warehouse.messages.error.title',
        ),
        detail: this.translateService.instant(
          'logical_warehouse.messages.error.invalid_ward',
        ),
        life: 6000,
      });
      this.submitting.set(false);
      return;
    }

    // Final validation: ensure all codes are valid numbers
    if (
      !provinceCode ||
      isNaN(provinceCode) ||
      !cityCode ||
      isNaN(cityCode) ||
      !districtCode ||
      isNaN(districtCode)
    ) {
      console.error('Invalid codes:', {
        provinceCode,
        cityCode,
        districtCode,
      });
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'logical_warehouse.messages.error.title',
        ),
        detail: 'Mã địa chỉ không hợp lệ. Vui lòng chọn lại tỉnh/xã.',
        life: 6000,
      });
      this.submitting.set(false);
      return;
    }

    if (this.isEditMode()) {
      // Map internal structure to API format for update
      const structure = this.mapInternalStructureToApiForUpdate();

      const updateData: WarehouseUpdateWithHierarchyDTO = {
        name: formData.name,
        code: formData.code,
        typeCode: formData.typeCode,
        status: formData.status !== undefined ? formData.status : true,
        provinceCode: provinceCode,
        provinceName: provinceName,
        cityCode: cityCode,
        cityName: cityName,
        districtCode: districtCode,
        districtName: districtName,
        detailAddress: formData.address,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        description: formData.description,
        length: formData.length ? Math.round(formData.length * 100) : undefined, // Convert m to cm
        width: formData.width ? Math.round(formData.width * 100) : undefined, // Convert m to cm
        height: formData.height ? Math.round(formData.height * 100) : undefined, // Convert m to cm
        zones: structure.zones,
        aisles: structure.aisles,
        racks: structure.racks,
      };

      this.logicalWarehouseService
        .updateLogicalWarehouseWithHierarchy(
          this.warehouseData()!.id!,
          updateData,
        )
        .subscribe({
          next: () => {
            this.submitting.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();

            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant(
                'logical_warehouse.messages.success.title',
              ),
              detail: this.translateService.instant(
                'logical_warehouse.messages.success.updated',
              ),
              life: 5000,
            });

            this.cdr.detectChanges();

            setTimeout(() => {
              this.router.navigate(['/warehouse/logical']);
            }, 2000);
          },
          error: (error: HttpErrorResponse) => {
            const fallbackKey =
              'logical_warehouse.messages.error.failed_to_update';
            this.errorDialogService.showError(
              this.errorTranslatorService.translate(
                error.error.message,
                fallbackKey,
              ),
            );
            this.submitting.set(false);
          },
        });
    } else {
      // Map internal structure to API format for create
      const structure = this.mapInternalStructureToApiForCreate();

      // Remove any id fields from structure to ensure clean create payload
      const cleanedStructure = {
        zones: structure.zones
          ? this.removeIdFromObject(structure.zones)
          : undefined,
        aisles: structure.aisles
          ? this.removeIdFromObject(structure.aisles)
          : undefined,
        racks: structure.racks
          ? this.removeIdFromObject(structure.racks)
          : undefined,
      };

      const createData: WarehouseCreateWithHierarchyDTO = {
        name: formData.name,
        code: formData.code,
        typeCode: formData.typeCode,
        status: formData.status !== undefined ? formData.status : true,
        provinceCode: provinceCode,
        provinceName: provinceName,
        cityCode: cityCode,
        cityName: cityName,
        districtCode: districtCode,
        districtName: districtName,
        detailAddress: formData.address,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        description: formData.description,
        length: formData.length ? Math.round(formData.length * 100) : undefined, // Convert m to cm
        width: formData.width ? Math.round(formData.width * 100) : undefined, // Convert m to cm
        height: formData.height ? Math.round(formData.height * 100) : undefined, // Convert m to cm
        zones: cleanedStructure.zones,
        aisles: cleanedStructure.aisles,
        racks: cleanedStructure.racks,
      };

      this.logicalWarehouseService
        .createLogicalWarehouseWithHierarchy(createData)
        .subscribe({
          next: () => {
            this.submitting.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();

            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant(
                'logical_warehouse.messages.success.title',
              ),
              detail: this.translateService.instant(
                'logical_warehouse.messages.success.created',
              ),
              life: 5000,
            });

            this.cdr.detectChanges();

            // Check if there's a returnUrl to navigate back to
            const returnUrl = this.route.snapshot.queryParams['returnUrl'];
            if (returnUrl) {
              setTimeout(() => {
                this.router.navigateByUrl(returnUrl);
              }, 2000);
            } else {
              setTimeout(() => {
                this.router.navigate(['/warehouse/logical']);
              }, 2000);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.showApiError(
              error,
              'logical_warehouse.messages.error.failed_to_create',
            );
            this.submitting.set(false);
          },
        });
    }
  }

  onCancel(): void {
    // Reset structure when canceling (only in add mode)
    if (this.isAddMode()) {
      this.structureData.set({ zones: [], aisles: [], racks: [] });
    }

    // Check if there's a returnUrl to navigate back to
    const returnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(['/warehouse/logical']);
    }
  }

  canDeactivate(
    _currentRoute: ActivatedRouteSnapshot,
    _currentState: RouterStateSnapshot,
    _nextState?: RouterStateSnapshot
  ): boolean | UrlTree {
    if (this.skipCanDeactivate || this.submitting()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  onSectionAction(action: any): void {
    // Handle section actions if needed
  }

  onTableAction(action: any): void {
    // Handle table actions if needed
  }
}
